import { useState, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { blockersOf, patchWithoutDecision } from '../blocked';
import { decisionFromBlockerGroup } from '../decisions';
import Icon, { type IconName } from './Icon';
import Priority from './Priority';
import { useApp } from '../store';
import { useEscapeToClose } from '../motion';
import { confirmDialog } from '../confirm';
import { staleTodoCards, STALE_TODO_DAYS } from '../data-quality';
import { needsAttention, attentionChip, type AttentionItem, type AttnFix } from '../needs-attention';
import NeedsAttentionPanel, { muteKey } from './NeedsAttentionPanel';
import { AttentionChipsContext, type CardChip } from './AttentionChips';
import { criticalPath } from '../critical-path';
import type { DepCard } from '../dependencies';
import type { Issue, IssueStatus } from '../types';
import { EMPTY_COLUMN, PRIORITY_ORDER, SIZE_LABEL, STATUS_LABELS, labelColour, liveLabels } from '../types';
import IssueCard from './IssueCard';
import BulkBar from './BulkBar';
import { useSelection } from '../selection';
import { offscreenCue, type BoardCol, type OffscreenCue } from '../board-offscreen';
import QuickAdd from './QuickAdd';
import { useSampleBoard, useSampleInert } from './SampleStrip';
import GridView from './GridView';
import CalendarView from './CalendarView';
import Expander from './Expander';
import BoardMetricsStrip from './BoardMetricsStrip';
import HandoffPrompt from './HandoffPrompt';
import InServiceHeader from './InServiceHeader';
import GoLiveRibbon from './GoLiveRibbon';
import ProjectGuide from './ProjectGuide';
import WheelsUp from './WheelsUp';
import {
  unblockActions,
  unblockEffect,
  stuckSummary,
  leverageTitle,
  downstreamCount,
  verifyResolve,
  resolveMessage,
  resolveIsBacked,
} from '../unblock';
import { readyFirst } from '../next-up';
import { inSprint, inOrder as sprintsInOrder, running as runningSprint, timeLeft, SPRINT_STATE_LABEL } from '../sprints';
import type { Sprint } from '../sprints';
import { canMoveCards, canCreateIssues, ownedByBoard, type IssueSource } from '../ownership';
import { can, ROLE_LABEL } from '../permissions';
import { outstandingChecks, doneAllowed } from '../done';
import { stepsFor } from '../setup';
import { isProjectMode, boardColumnsFor } from '../lifecycle';
import { displayName } from '../people';
import { askStartGate } from '../start-gate';
import VendorTag from './VendorTag';
import { initials, asDayMonth } from '../utils';
import { assigneeName, assistantsOf, assistantAssignee } from '../assistant';
import { boardAssignees } from '../projects';
import { DoerPrompt } from './DoerPrompt';
import type { PickOption } from './PickField';
import { livePhases } from '../phases';
import { epicBackground } from '../epics';

// How the board's columns can be laid into horizontal lanes. "none" is the
// ordinary single board; the others split every column by who owns the card,
// which phase it is in, which epic it belongs to, or which area of the product
// it sits in — the pm-witness's who/workload and plan-order views, plus grouping
// by workstream (epic) or by standing domain (area), so a board run in kanban can
// swimlane by area the same way the Backlog already groups by it. #462 parity.
// 'assignee' groups by the Accountable person; 'responsible' by the doer — the
// two halves of the two-part assignee (Feature A).
type BoardGroup = 'none' | 'assignee' | 'responsible' | 'phase' | 'epic' | 'area' | 'label';

// The focus control (jump to one lane) reads differently per grouping: it shows
// "Everyone" over people, "All phases" over phases, "All epics" over epics — and
// the grouping's own icon and search wording — so the same widget names whatever
// it is grouping. 'none' never shows the control; its entries are only fallbacks.
const GROUP_ALL: Record<BoardGroup, string> = {
  none: 'All',
  assignee: 'Everyone',
  responsible: 'Everyone',
  phase: 'All Phases',
  epic: 'All Epics',
  area: 'All Areas',
  label: 'All Labels',
};
const GROUP_SEARCH: Record<BoardGroup, string> = {
  none: 'Search…',
  assignee: 'Search people…',
  responsible: 'Search people…',
  phase: 'Search phases…',
  epic: 'Search epics…',
  area: 'Search areas…',
  label: 'Search labels…',
};
const GROUP_ICON: Record<BoardGroup, IconName> = {
  none: 'board',
  assignee: 'users',
  responsible: 'person',
  phase: 'calendar',
  epic: 'list',
  area: 'grid',
  label: 'tag',
};
// The segmented control's own labels — reused verbatim by the collapsed "Group: …"
// dropdown that replaces the segments on a narrow desktop window (insertion order is
// the on-screen order of the segments).
const GROUP_LABEL: Record<BoardGroup, string> = {
  none: 'None',
  assignee: 'Accountable',
  responsible: 'Responsible',
  phase: 'Phase',
  epic: 'Epic',
  area: 'Area',
  label: 'Label',
};

// How the cards within a column are ordered. The board has no manual drag-order,
// so "priority" (the long-standing default) leads rather than a manual mode.
type BoardSort = 'priority' | 'due' | 'age' | 'updated';
const SORT_LABEL: Record<BoardSort, string> = {
  priority: 'Priority',
  due: 'Due date',
  age: 'Longest in progress',
  updated: 'Recently updated',
};

// Finished work outnumbers live work roughly 5 to 1 here, so the Done column shows
// only the newest few until asked for more. Without this it is a wall you scroll past.
const DONE_PREVIEW = 5;
// The optional on-board Backlog column (0218) is a window on the queue, not the queue
// itself: it shows only the first few so a deep backlog can never flood the board, and
// its "more" opens the full Backlog view rather than expanding in place.
const BACKLOG_PREVIEW = 4;

// An empty column's two lines read differently by lane. The heading is "All caught
// up" for the active lanes, where empty is a good state; the BACKLOG and DONE are
// not "caught up" states, so they name themselves. The drag invitation is shown
// only where dropping a card there means STARTING work: the backlog is a holding
// pen you PARK work into (not start from), and done is an outcome you never drag
// work into to begin. A '' entry suppresses the invitation for that lane.
const EMPTY_COL_HEADING: Partial<Record<IssueStatus, string>> = {
  backlog: 'Backlog is clear',
  done: 'Nothing finished yet',
  wontdo: 'Nothing set aside',
};
const EMPTY_COL_DRAG: Partial<Record<IssueStatus, string>> = {
  backlog: 'Drag a card here to park it for later.',
  done: '',
  wontdo: '',
};

// Everything startable, up to a point. A list long enough to be a plan is no
// longer a recommendation — past this it is just the backlog again.
const NEXT_UP_SHOWN = 4;

/**
 * The board's horizontal scroll surface, with the off-screen cues over it.
 *
 * When In Staging is on there are five fixed-width columns and the board scrolls
 * (3.19.2) — but a board whose only cards are in Done used to read as empty,
 * with Done stranded off the right and nothing to say so. This wraps the grid
 * and, only while it overflows, draws a neutral edge fade and — pointing at the
 * nearest hidden non-empty column — a "Done · 14 →" marker that scrolls it in.
 * Nothing shrinks and nothing auto-scrolls: the work is advertised, not moved.
 */
function BoardScroll({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const target = useRef<HTMLElement | null>(null);
  const [cue, setCue] = useState<OffscreenCue>({ left: false, right: false, marker: null });

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    const colEls = Array.from(el.querySelectorAll<HTMLElement>('.column'));
    const cols: BoardCol[] = colEls.map((c) => ({
      left: c.offsetLeft,
      width: c.offsetWidth,
      count: Number(c.dataset.count || '0'),
      label: c.dataset.label || '',
      status: c.dataset.status || '',
    }));
    const next = offscreenCue(cols, el.scrollLeft, el.clientWidth);
    target.current = next.marker
      ? colEls.find((c) => c.offsetLeft === next.marker!.left) || null
      : null;
    setCue((prev) =>
      prev.left === next.left &&
      prev.right === next.right &&
      prev.marker?.label === next.marker?.label &&
      prev.marker?.count === next.marker?.count
        ? prev
        : next
    );
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
    // Re-subscribing every render keeps the cue honest as cards (and so the
    // columns' counts and widths) change; the cleanup makes it leak-free.
  });

  const reveal = () => target.current?.scrollIntoView({ inline: 'end', block: 'nearest', behavior: 'smooth' });

  // DRAG TO THE EDGE, AND THE BOARD FOLLOWS. When a card is dragged toward the
  // right (or left) edge of the board, scroll that way so an off-screen column —
  // typically Done, five columns in — comes into view and can be dropped onto,
  // instead of the card having to be let go first to go and find it. A rAF loop
  // keeps scrolling while the pointer stays in the edge zone (dragover stops
  // firing when the pointer is held still, so the loop, not the event, drives it).
  const autoScroll = useRef<{ dir: number; raf: number } | null>(null);
  const stopAutoScroll = () => {
    if (autoScroll.current) {
      cancelAnimationFrame(autoScroll.current.raf);
      autoScroll.current = null;
    }
  };
  const stepAutoScroll = () => {
    const el = ref.current;
    const st = autoScroll.current;
    if (!el || !st) return;
    el.scrollLeft += st.dir * 16;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    const atStart = el.scrollLeft <= 0;
    if ((st.dir > 0 && atEnd) || (st.dir < 0 && atStart)) {
      stopAutoScroll();
      return;
    }
    st.raf = requestAnimationFrame(stepAutoScroll);
  };
  const onDragOver = (e: React.DragEvent) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const EDGE = 72; // the strip near each edge that arms the scroll
    let dir = 0;
    if (e.clientX > rect.right - EDGE && el.scrollLeft + el.clientWidth < el.scrollWidth - 1) dir = 1;
    else if (e.clientX < rect.left + EDGE && el.scrollLeft > 0) dir = -1;
    if (dir === 0) {
      stopAutoScroll();
    } else if (autoScroll.current) {
      autoScroll.current.dir = dir;
    } else {
      autoScroll.current = { dir, raf: requestAnimationFrame(stepAutoScroll) };
    }
  };
  // A drop or an abandoned drag can end with the pointer still in the edge zone,
  // where no further dragover arrives to clear it — so stop on the global dragend
  // too, and on unmount.
  useEffect(() => {
    window.addEventListener('dragend', stopAutoScroll);
    return () => {
      window.removeEventListener('dragend', stopAutoScroll);
      stopAutoScroll();
    };
  }, []);

  return (
    <div className="board-scroll">
      <div
        className={`board${cue.left || cue.right ? ' is-overflowing' : ''}`}
        ref={ref}
        onDragOver={onDragOver}
        onDrop={stopAutoScroll}
        onDragLeave={(e) => {
          // Only when the pointer truly leaves the board, not on the way between
          // its own columns (where relatedTarget is still inside it).
          if (!ref.current || !ref.current.contains(e.relatedTarget as Node)) stopAutoScroll();
        }}
      >
        {children}
      </div>
      {cue.left && <div className="board-fade board-fade-left" aria-hidden="true" />}
      {cue.right && <div className="board-fade board-fade-right" aria-hidden="true" />}
      {cue.marker && (
        <button
          type="button"
          className="board-offscreen"
          /* The count bubble is tinted by the off-screen column's own status, so
             the cue works for Done, In Review, In Staging or any preset — not a
             hardcoded green. The column's name is in the hover title. */
          style={{
            '--mk': `var(--s-${cue.marker.status})`,
            '--mk-ink': `var(--s-${cue.marker.status}-ink)`,
          } as React.CSSProperties}
          onClick={reveal}
          onKeyDown={(e) => {
            if (e.key === 'End' || e.key === 'ArrowRight') {
              e.preventDefault();
              reveal();
            }
          }}
          title={`Scroll to ${cue.marker.label} (${cue.marker.count})`}
          aria-label={`Scroll to ${cue.marker.label}, ${cue.marker.count} cards`}
        >
          <Icon name="arrow" size={16} />
          <span className="board-offscreen-count">{cue.marker.count}</span>
        </button>
      )}
    </div>
  );
}

export default function Board() {
  const issues = useApp((s) => s.issues);
  const search = useApp((s) => s.search);
  const moveIssue = useApp((s) => s.moveIssue);
  const setView = useApp((s) => s.setView);
  const showInBacklog = useApp((s) => s.showInBacklog);
  const openIssue = useApp((s) => s.openIssue);
  const role = useApp((s) => s.shownRole);
  const source = useApp((s) => s.meta.source ?? 'tracker');
  const canMove = canMoveCards(role, source);
  const sample = useSampleBoard();
  const inert = useSampleInert();
  // The same rule as the top bar's New issue. A board fed from a tracker does
  // not take issues raised here, so a + on the column would offer something the
  // save then refuses.
  const canAdd = canCreateIssues(role, source as IssueSource);
  const canEdit = can(role, 'editIssue');
  /** Whether the board owns a field, or a tracker would overwrite it. */
  const mineToEdit = (f: Parameters<typeof ownedByBoard>[0]) => ownedByBoard(f, source as IssueSource);
  const startCreate = useApp((s) => s.startCreate);
  const updateIssue = useApp((s) => s.updateIssue);
  const recordBulk = useApp((s) => s.recordBulk);
  const logDecision = useApp((s) => s.logDecision);
  const bulkMoveAll = useApp((s) => s.bulkMove);
  const showFlash = useApp((s) => s.showFlash);
  // ── Bulk selection (project-bulk-card-changes) — the shared, view-agnostic store.
  const selIds = useSelection((s) => s.ids);
  const selectMode = useSelection((s) => s.selectMode);
  const toggleMode = useSelection((s) => s.toggleMode);
  const exitSelect = useSelection((s) => s.exitSelect);
  const addSel = useSelection((s) => s.add);
  const removeSel = useSelection((s) => s.remove);
  const setSelOrder = useSelection((s) => s.setOrder);
  const clearSel = useSelection((s) => s.clear);
  const myEmail = (useApp((s) => s.user?.email) ?? '').toLowerCase();
  // The drop zone under the cursor, as "<lane>:<status>", so a drag highlights
  // only the one column being dropped into — not every lane's column of that
  // status when the board is split into swimlanes.
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [showAllDone, setShowAllDone] = useState(false);
  // "Show it" from a partial-move result filters the board to the refused cards
  // (project-bulk-card-changes); null is the ordinary board.
  const [onlyIds, setOnlyIds] = useState<Set<string> | null>(null);
  // What refused to move, and why — a card that springs back with no explanation
  // reads as a broken board rather than an unmet standard.
  const [refused, setRefused] = useState<{ ref: string; missing: string[] } | null>(null);
  // The card whose doer we are asking about (A2), and the card+mover pairs this
  // session has already asked — so a dismissal is honoured and we never nag.
  const [doerAsk, setDoerAsk] = useState<Issue | null>(null);
  const dismissedDoer = useRef<Set<string>>(new Set());
  const meta = useApp((s) => s.meta);
  // A board run as a continuous service is not a project (#435), so none of the
  // project-setup scaffolding — the "set up & run this project" guide, the
  // "this project has not been set up" offer — belongs on it. Absent reads as a
  // project, so existing boards are unaffected until one is set to continuous.
  const isProject = isProjectMode(meta);
  /* The board's own columns: the four defaults, plus In Staging spliced in before
     In Review on a board that turned it on. Everything that asks "is this card on
     the board" or renders a column reads this, so a non-staging board is unchanged
     and a staging board gains exactly one column. */
  const cols = boardColumnsFor(meta);
  /*
   * STALE TO-DO STRIP (project-idle-nudge) — the same stale-To-do gap the Insights
   * Data quality panel surfaces, brought to the board face where the work is. A
   * To-do card with no due date that has not moved in STALE_TODO_DAYS working days
   * is WIP that will never leave the column on its own; it inflates the board's
   * work-in-progress count and its aging. Shown only to someone who runs the board
   * (manager+), only when the board's data-quality surfacing is on (the same admin
   * switch as the panel), only when there is at least one, and until waved away for
   * the session — a plain bordered notice, never a nag. One tap moves the batch to
   * Backlog (reversible), the same action and wording the panel offers.
   */
  const moveIssues = useApp((s) => s.moveIssues);
  const dataQualityOn = meta.dataQualityPanel !== false;
  const canHousekeep = can(role, 'editSettings');
  // The workspace working calendar (0216) — the strip counts working days only, so a
  // workspace-wide close is not held against a queued card.
  const workCalendar = useApp((s) => s.workCalendar);
  const staleTodos = useMemo(() => staleTodoCards(issues, Date.now(), workCalendar), [issues, workCalendar]);
  const [staleBusy, setStaleBusy] = useState(false);
  const [staleDismissed, setStaleDismissed] = useState(false);
  const showStaleStrip = dataQualityOn && canHousekeep && !staleDismissed && staleTodos.length > 0;
  async function moveStaleToBacklog() {
    const ids = staleTodos.map((i) => i.id);
    const n = ids.length;
    const ok = await confirmDialog({
      title: 'Move to Backlog',
      body: `Move ${n} stale To-Do ${n === 1 ? 'card' : 'cards'} to the Backlog? They stop padding the work-in-progress count and the aging average; move any back whenever you like.`,
      confirmLabel: `Move ${n}`,
      tone: 'caution',
    });
    if (!ok) return;
    setStaleBusy(true);
    try { await moveIssues(ids, 'backlog'); } finally { setStaleBusy(false); }
  }
  const profiles = useApp((s) => s.profiles);
  const access = useApp((s) => s.access);
  const setupDismissed = useApp((s) => s.setupDismissed);
  const startSetup = useApp((s) => s.startSetup);
  const dismissSetup = useApp((s) => s.dismissSetup);

  const q = search.trim().toLowerCase();
  const visible = q
    ? issues.filter((i) => i.title.toLowerCase().includes(q) || i.ref.toLowerCase().includes(q))
    : issues;

  const backlogCount = visible.filter((i) => i.status === 'backlog').length;

  /**
   * NEXT UP ASKS A DIFFERENT SET ON A SPRINT BOARD.
   *
   * On flow, "what next" is asked of everything: the whole board is the pool,
   * and the answer is the single best thing to start anywhere on it. In a
   * timebox the question is "what is left in THIS one", and answering it with a
   * card from outside the sprint is not a small imprecision — it is the board
   * telling somebody to work on something the team agreed not to do this
   * fortnight. That is the entire difference between the two ways of working,
   * so it happens here rather than being left to whoever reads the panel.
   *
   * WITH SPRINTS ON AND NONE RUNNING it falls back to the whole board, which is
   * the honest answer: no timebox has been committed to, so there is nothing
   * narrower to ask. Showing an empty panel would read as "there is nothing to
   * do" on a board with a hundred cards on it.
   */
  const sprints = useApp((s) => s.sprints);
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const groupsOf = useApp((s) => s.groupsOf);
  const phases = useApp((s) => s.phases);
  const areas = useApp((s) => s.areas);
  const labelReg = useApp((s) => s.labels);
  const issueTypes = useApp((s) => s.issueTypes);

  /**
   * NEEDS ATTENTION (Metrics Layer A) — the board-wide "what to act on, and WHY"
   * read behind the toolbar pill. Built on the SAME CPM the Timeline draws (so its
   * schedule causes can never disagree with the critical path) and the shipped
   * signals (reallyBlocked, card-age, misfiled, cardDueState) via needs-attention.ts.
   */
  const attnResult = useMemo(() => {
    const toISO = (ms: number) => {
      const d = new Date(ms);
      const p = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    };
    const project = projects.find((p) => p.id === projectId);
    const goLiveISO = project?.goLive ? toISO(project.goLive) : null;
    const live = issues.filter((i) => i.status !== 'wontdo');
    const depCards: DepCard[] = live.map((i) => ({
      id: i.id, ref: i.ref, status: i.status, startsOn: i.startsOn, dueDate: i.dueDate, blockerRefs: i.blockerRefs,
    }));
    const cpm = criticalPath(depCards, goLiveISO, workCalendar);
    return needsAttention({
      issues, phases, cpm, now: Date.now(), cal: workCalendar,
      issueTypes: issueTypes.map((t) => t.name),
      // The sprint/size gaps are raised only on a board that runs on sprints; a
      // Flow/builds board has no sprints, so "No sprint" is never a real gap there.
      cadence: project?.cadence,
    });
  }, [issues, phases, projects, projectId, workCalendar, issueTypes]);

  const [showAttention, setShowAttention] = useState(false);
  // Escape closes the Needs Attention panel — a way out that matches every other pane
  // (L39). An open card or dialog takes Escape first (the `.overlay` guard), so this never
  // steals it from a modal sitting on top.
  useEffect(() => {
    if (!showAttention) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('.overlay')) return;
      setShowAttention(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAttention]);
  const attnMuteKey = `fd.attn.muted.${projectId ?? 'board'}`;
  const [attnMuted, setAttnMuted] = useState<Set<string>>(() => {
    try { const raw = localStorage.getItem(attnMuteKey); return new Set(raw ? (JSON.parse(raw) as string[]) : []); } catch { return new Set(); }
  });
  const toggleAttnMute = (item: AttentionItem) => {
    setAttnMuted((prev) => {
      const next = new Set(prev);
      const k = muteKey(item);
      if (next.has(k)) next.delete(k); else next.add(k);
      try { localStorage.setItem(attnMuteKey, JSON.stringify([...next])); } catch { /* best effort — a quiet per-browser preference */ }
      return next;
    });
  };
  const sprintName = (id: string | null | undefined) => (id ? sprints.find((s) => s.id === id)?.name : undefined);
  const issueByRef = useMemo(() => new Map(issues.map((i) => [i.ref, i] as const)), [issues]);
  // Needs Attention's "Fix →" opens the card onto the field the engine named as the
  // fix, so the change is one scroll-free move away (every action intentional). The
  // engine's AttnFix maps to the modal's editField focus targets; a fix with no one
  // field to land on (review / open-card) just opens the card.
  const FIX_TO_FIELD: Partial<Record<AttnFix, 'dates' | 'assignee' | 'sprint' | 'size' | 'status'>> = {
    'set-dates': 'dates', assign: 'assignee', 'add-to-sprint': 'sprint', 'set-size': 'size', 'move-to-status': 'status',
  };
  const openCardByRef = (ref: string, fix?: AttnFix) => {
    const i = issueByRef.get(ref);
    if (i) openIssue(i.id, fix ? FIX_TO_FIELD[fix] : undefined);
  };

  // The one worst chip per flagged card, for the per-card face badge. A card that
  // already shows a blocker line keeps that as its first read (R23), so the tile
  // suppresses the chip there — see IssueCard; here we expose every card's chip.
  const attnChipByRef = useMemo(() => {
    const m = new Map<string, CardChip>();
    for (const ref of new Set(attnResult.items.map((it) => it.ref))) {
      const c = attentionChip(attnResult, ref);
      if (c) m.set(ref, c);
    }
    return m;
  }, [attnResult]);
  /*
   * ── LAYING THE BOARD INTO SWIMLANES ───────────────────────────────────────
   * Group every column by who owns the card, which phase it is in, which epic it
   * belongs to, or which area of the product it sits in — the pm-witness's
   * who/workload and plan-order views, plus workstream and domain. Per person and
   * per board,
   * kept in this browser like the sprint picker and sort below: two people can
   * reasonably want different lanes, and it is nobody else's decision. Off by
   * default, because the plain board is the one everybody already knows.
   */
  const groupKey = `flightdeck-board-group-${projectId}`;
  const [groupBy, setGroupBy] = useState<BoardGroup>(() => {
    try {
      const v = localStorage.getItem(groupKey);
      return v === 'assignee' || v === 'responsible' || v === 'phase' || v === 'epic' || v === 'area' || v === 'label'
        ? v
        : 'none';
    } catch {
      return 'none';
    }
  });
  const setGroup = (g: BoardGroup) => {
    setGroupBy(g);
    try {
      localStorage.setItem(groupKey, g);
    } catch {
      /* private browsing — the board still groups, it just forgets next load */
    }
  };
  /*
   * How each column is ordered, remembered the same way and for the same reason.
   * Priority has always been the order; the others are lenses a person can reach
   * for — soonest due, oldest in flight, most recently touched.
   */
  const sortKey = `flightdeck-board-sort-${projectId}`;
  const [sortBy, setSortBy] = useState<BoardSort>(() => {
    try {
      const v = localStorage.getItem(sortKey);
      return v === 'due' || v === 'age' || v === 'updated' ? v : 'priority';
    } catch {
      return 'priority';
    }
  });
  const setSort = (s: BoardSort) => {
    setSortBy(s);
    try {
      localStorage.setItem(sortKey, s);
    } catch {
      /* private browsing — the order still applies, it just forgets next load */
    }
  };

  /*
   * KANBAN OR GRID — the same board, two layouts (#508). The switch sits in the
   * board header below; the choice is kept in this browser like group-by and
   * sort above, per board, because it is a way of looking, not a board setting
   * two people must share. Default kanban — the layout everybody already knows.
   */
  const modeKey = `flightdeck-board-mode-${projectId}`;
  const [boardMode, setBoardMode] = useState<'kanban' | 'grid' | 'calendar'>(() => {
    try {
      const m = localStorage.getItem(modeKey);
      return m === 'grid' || m === 'calendar' ? m : 'kanban';
    } catch {
      return 'kanban';
    }
  });
  const setMode = (m: 'kanban' | 'grid' | 'calendar') => {
    setBoardMode(m);
    // Picking a board view closes the Needs Attention panel — it replaces the board pane,
    // so without this it would sit on top of the view you just asked for (L39).
    setShowAttention(false);
    try {
      localStorage.setItem(modeKey, m);
    } catch {
      /* private browsing — the layout still switches, it just forgets next load */
    }
  };

  /* Sort is a compact glyph button, not a full-width field — it opens a small
     menu. Closes on a click anywhere outside the button-and-menu, and on Escape,
     the same way every other menu in the app does (see BacklogFilters). */
  const [sortOpen, setSortOpen] = useState(false);
  const sortWrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sortOpen) return;
    const away = (e: MouseEvent) => {
      const hit = e.target as Element | null;
      if (!hit?.closest?.('.board-sort-wrap')) setSortOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSortOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [sortOpen]);

  /* GROUP-BY WHEN TIGHT — on a narrow desktop window the six-option segmented control
     collapses to a single "Group: … ▾" dropdown (shown/hidden by .board-tools.is-tight,
     below). It is the app's own menu-trigger pattern: closes on an outside click or
     Escape, exactly like Sort and Focus. */
  const [groupOpen, setGroupOpen] = useState(false);
  const groupWrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!groupOpen) return;
    const away = (e: MouseEvent) => {
      const hit = e.target as Element | null;
      if (!hit?.closest?.('.board-group-wrap')) setGroupOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setGroupOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [groupOpen]);

  /* MORE — when tight, the "Collapse all" action (only ever present while the board is
     grouped into lanes) folds into this small overflow menu, so the row stays one line. */
  const [moreOpen, setMoreOpen] = useState(false);
  const moreWrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!moreOpen) return;
    const away = (e: MouseEvent) => {
      const hit = e.target as Element | null;
      if (!hit?.closest?.('.board-more-wrap')) setMoreOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [moreOpen]);

  // DEMOTE, DON'T WRAP (Vinnie, 4 Nov) — the toolbar changes form instead of wrapping
  // when it will not fit. State and refs live here; the measuring effect is further down
  // (it depends on `grouped`, which widens the control set and is defined below).
  const toolsRef = useRef<HTMLDivElement>(null);
  const [tight, setTight] = useState(false);
  const wideNeed = useRef(0);

  /*
   * FOCUS ON ONE LANE — offered whenever the board is grouped (by assignee, phase
   * or epic). Grouping draws a lane per value; on a busy board that is a dozen
   * full-width lanes to scroll past to read one. Focus narrows the board to a
   * single lane's cards, laid across the columns like the ordinary board (no lane
   * band — the control already names it). The empty string is the default all-
   * lanes view. Not persisted: it is a momentary "just show me this one", not a
   * saved board setting like group-by or sort.
   */
  const [focus, setFocus] = useState('');
  const [focusOpen, setFocusOpen] = useState(false);
  const [focusLook, setFocusLook] = useState('');
  const focusWrap = useRef<HTMLDivElement>(null);
  /* Any change of grouping drops the focus. A lane key from one grouping (a
     person) means nothing under another (a phase or epic), so a carried-over
     focus would point at a lane that no longer exists. */
  useEffect(() => {
    setFocus('');
    setFocusOpen(false);
  }, [groupBy]);
  useEffect(() => {
    if (!focusOpen) return;
    const away = (e: MouseEvent) => {
      const hit = e.target as Element | null;
      if (!hit?.closest?.('.board-focus-wrap')) setFocusOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFocusOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [focusOpen]);
  /*
   * THE SPRINT PICKER — a compact menu-trigger on the toolbar, the sibling of
   * Sort and Focus. Opened and closed the same way every other toolbar menu is:
   * a click anywhere outside the button-and-menu, or Escape.
   */
  const [sprintOpen, setSprintOpen] = useState(false);
  // Type-to-filter inside the menu, so a board with many finished sprints stays
  // scannable. Cleared whenever the menu closes, so it reopens fresh.
  const [sprintQuery, setSprintQuery] = useState('');
  const sprintWrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sprintOpen) setSprintQuery('');
  }, [sprintOpen]);
  useEffect(() => {
    if (!sprintOpen) return;
    const away = (e: MouseEvent) => {
      const hit = e.target as Element | null;
      if (!hit?.closest?.('.board-sprint-wrap')) setSprintOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSprintOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [sprintOpen]);

  const thisSprint = meta.cadence === 'sprints' ? runningSprint(sprints) : undefined;
  const pool = thisSprint ? inSprint(visible, thisSprint.id) : visible;

  /**
   * ── SHOWING ONLY THE SPRINT'S CARDS ────────────────────────────────────
   *
   * Asked for by TRINRE, 18 August 2026: "when in a sprint, only the items in
   * the sprint should show up on the active board and everything else that is
   * not being worked on in the sprint should be in the backlog."
   *
   * IT HIDES, IT DOES NOT MOVE — Vinnie settled that the next day: "Just filter
   * the view, don't move anything… they can hide the cards not in a sprint for
   * them so nothing is lost." The distinction is the whole design. Rewriting
   * every unplanned card to `backlog` would be one press changing hundreds of
   * rows across everybody's board, recorded as somebody's decision and
   * undoable only card by card. This changes nothing but what one person is
   * looking at.
   *
   * SO IT IS PER PERSON AND PER BOARD, kept in this browser. It is not a
   * project setting: two people on one sprint can reasonably want different
   * answers — somebody carrying unplanned support work needs to see it, and
   * that is not a decision for whoever changed the setting last.
   *
   * OFF UNTIL SOMEBODY ASKS. Vinnie's call over defaulting it on: a board that
   * silently shows fewer cards than yesterday, to everybody, on the morning of
   * an upgrade, is indistinguishable from work having gone missing.
   */
  /*
   * WHICH SPRINT'S CARDS THE BOARD SHOWS — the picker on the toolbar.
   *
   * It replaces the old single on/off "Only {active sprint}" switch. That could
   * narrow the board to the running fortnight and nothing else; this lets a
   * person filter by ANY sprint — the one running, one still to come, or a
   * finished one being reopened for a retro or an audit — with "All sprints" the
   * default that filters nothing.
   *
   * Still per person and per board, kept in this browser like group-by and sort:
   * two people on one board can reasonably want different views, and it is
   * nobody else's decision. The empty string is "all sprints".
   */
  const sprintKey = `flightdeck-board-sprint-${projectId}`;
  const [sprintPick, setSprintPick] = useState(() => {
    try {
      return localStorage.getItem(sprintKey) ?? '';
    } catch {
      return ''; // private browsing — showing everything is the safe answer
    }
  });
  const pickSprint = (id: string) => {
    setSprintPick(id);
    try {
      if (id) localStorage.setItem(sprintKey, id);
      else localStorage.removeItem(sprintKey);
    } catch {
      /* nothing to remember it with, which only costs the next page load */
    }
  };
  /*
   * THE PICKED SPRINT, OR NOTHING. A stored id whose sprint has since been
   * deleted falls back to all rather than to an empty board — the same fallback
   * the lane focus makes when its lane is gone. The picker only exists on a board
   * that works in sprints, so on a flow board this is always nothing.
   */
  const pickedSprint =
    sprintPick && meta.cadence === 'sprints' ? sprints.find((s) => s.id === sprintPick) : undefined;
  const filtering = Boolean(pickedSprint);
  const shown = filtering ? inSprint(visible, pickedSprint!.id) : visible;
  // "13–27 Oct", "29 Sep–12 Oct" — the sprint's dates, the month shown once when
  // start and end share it. The toolbar and the hidden-count line both read it.
  const sprintRange = (s: Sprint) => {
    const start = asDayMonth(s.startsOn);
    const end = asDayMonth(s.endsOn);
    const sameMonth = start.split(' ')[1] === end.split(' ')[1];
    return sameMonth ? `${start.split(' ')[0]}–${end}` : `${start}–${end}`;
  };
  /*
   * WHAT WAS ACTUALLY HIDDEN — counted in cards that were on the board.
   *
   * It was `visible.length - shown.length`, and `visible` is every issue on the
   * project: backlog cards included. The board has four columns and none of
   * them is Backlog, so those cards were never drawn — and the line announced
   * hiding seven cards when two had gone from the screen. A number that
   * overstates what just happened, in the one sentence written to reassure
   * somebody that nothing was lost, is worse than no sentence.
   *
   * Found on 20 August 2026 by a test that compared the number on screen with
   * the number of rows that actually disappeared.
   */
  const onTheBoard = (list: Issue[]) => list.filter((i) => cols.includes(i.status));
  const hiddenCount = onTheBoard(visible).length - onTheBoard(shown).length;

  /*
   * "Ready to start" is the not-yet-started work ON THE BOARD — the To-Do
   * column, unblocked. Deliberately NOT the backlog (that is the separate strip
   * below, "pull work onto the board") and NOT work already in progress or in
   * review: either would double-count the backlog line and overstate what is
   * actually startable, which read as two lines saying the same thing (Vinnie,
   * 23 Sep). readyFirst stays the shared ordering rule — only the SET it ranks
   * is scoped here, so the ordering still cannot drift from the epics'.
   */
  // The ranked SET is this board/sprint's To-Do column; the blocker lookup is the
  // WHOLE board (`issues`), so a dependent whose predecessor just finished — and
  // so has left the To-Do set — is correctly seen as freed and appears here (L164).
  const nextUp = readyFirst(pool.filter((i) => i.status === 'todo'), issues);

  // When nothing is ready, the useful question is not "what next" but "what would
  // release the most work" — so Next up answers that instead of showing an empty box.
  // Every action, not a top few — a hidden group is work you cannot see is blocked.
  // Next up is ONE thing: the item to start, or — when nothing can be started —
  // the single action that releases the most. The rest lives on Insights.
  // The same narrowing, for the same reason: "what would release the most work"
  // is about this fortnight's work when there is a fortnight.
  const actions = unblockActions(pool);
  const top = actions[0];
  const rest = nextUp.length > 0 ? [] : actions.slice(1);
  /**
   * The card that Assign to Me and Open Issue act on.
   *
   * The blocker itself where the blocker is a card — that is the work that has
   * to happen, and it is the one named in the title.
   *
   * Where the blocker is NOT a card — five items waiting on something nobody
   * has named — there is no single subject, and both buttons used to drop.
   * They stay now, on the first blocked item, because a design with three
   * buttons that shows one is not the design. The button prints the reference
   * it will open, so it always says what it is about to do.
   */
  const actOn = top?.blocker ?? top?.issues[0];
  // Whether the question is on screen. Not remembered between blockers: the
  // group under it changes as issues move, and a confirmation left open across
  // that change would be agreeing to something else.
  const [confirming, setConfirming] = useState(false);
  // The Next Up ready-list sits collapsed by default so the board columns start at
  // the top of the view; the bar's "Ready to start" pill glides it open on demand.
  const [readyOpen, setReadyOpen] = useState(false);
  // Escape dismisses the inline "resolve blocker" confirm, matching the app's
  // "Escape closes the transient layer" grammar — bubble phase, so it closes only
  // this panel and nothing behind it (interaction-witness, 4 Nov).
  useEscapeToClose(() => setConfirming(false), confirming);
  // Clearing a DECISION-type blocker from here captures what was decided (same as
  // the card), so the call the board was waiting on lands in the decision log
  // instead of evaporating — the gap on this path only (Vinnie, 25 Oct).
  const [decidedText, setDecidedText] = useState('');
  const [seedLogging, setSeedLogging] = useState(false);
  const [seedProblem, setSeedProblem] = useState('');
  const verdict = verifyResolve(top ?? {}, meta);
  // The leverage card's blocker is a decision when the cards it holds are waiting
  // on one (unblockActions groups by blockerType, so the group is uniform).
  const decisionClear = !!top && top.issues.some((i) => i.blockerType === 'decision');
  // What each waiting card was waiting on — shown read-only above the (empty)
  // "what was decided?" field so the reason is in view without being mistaken for
  // the answer. Usually one line (one card, or several held by the same call);
  // when the grouped cards were waiting on DIFFERENT decisions we show each tagged
  // with its ref, rather than passing one card's reason off as the whole group's.
  const decisionReasons = decisionClear
    ? top!.issues
        .map((i) => ({ ref: i.ref, text: blockersOf(i).find((e) => e.type === 'decision')?.text ?? '' }))
        .filter((r) => r.text)
    : [];
  const decisionReasonsDistinct = Array.from(new Set(decisionReasons.map((r) => r.text)));
  // One clear path for both buttons: a decision blocker strips the decision entry
  // from blocker_refs AND re-mirrors the scalars (patchWithoutDecision — so no
  // stale structured entry is left); otherwise the old scalar clear, as before.
  // Records the group as one change and closes the confirm.
  const clearLeverageBlocker = (group: Issue[]) => {
    group.forEach((i) =>
      decisionClear
        ? updateIssue(i.id, patchWithoutDecision(blockersOf(i)))
        : updateIssue(i.id, { blocked: '', blockerType: '', blockerTarget: '' })
    );
    recordBulk('cleared the blocker', group);
    setConfirming(false);
  };
  // What setting this project up would still ask. The same rule the wizard
  // uses, so the count on the offer is the number of questions that appear.
  const setupOutstanding = stepsFor({
    canSettings: can(role, 'editSettings'),
    meta,
    memberCount: access.emails.length,
    profile: profiles[myEmail],
  });
  /**
   * Everything stuck, counted once and split by whether it has a card.
   *
   * Every job, including the one shown above, because the line is a summary of
   * the whole situation rather than a footnote to the top recommendation. One
   * issue can be waiting on two things and so appear in two jobs, hence the Set:
   * counting it twice would promise more rows than the backlog then shows.
   *
   * Safe to treat every backlog item in view as stuck: this block only renders
   * when nothing can be started, and anything in the backlog that were NOT
   * blocked would be startable and would have prevented that.
   */
  const stuck = [...new Set(actions.flatMap((a) => a.issues))];
  const stuckInBacklog = stuck.filter((i) => i.status === 'backlog').length;
  const stuckOnBoard = stuck.length - stuckInBacklog;

  // WORK IS STARTING — ask who is doing it (A2), once. Only on the move INTO
  // In Progress, only when no doer is set yet, only if this person has not
  // already waved it away for this card. The move is optimistic, so the card is
  // already in the column by the time the prompt measures against it.
  const maybeAskDoer = (status: IssueStatus, id: string) => {
    if (status !== 'inprogress' || !canEdit || !myEmail) return;
    const issue = issues.find((i) => i.id === id);
    if (issue && !issue.responsible && !dismissedDoer.current.has(`${id}|${myEmail}`)) {
      setDoerAsk(issue);
    }
  };

  const drop = (status: IssueStatus) => async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
    if (!canMove || sample.readOnly) return;
    // dataTransfer is only readable while the event is live, so take the id now,
    // before any await in the gate below pools the event away.
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    // DRAGGING A SELECTED CARD DRAGS THE WHOLE SELECTION (project-bulk-card-changes).
    // When the card under the cursor is part of a multi-selection, the whole set
    // moves to this column as ONE bulk status move; a drag of an UNSELECTED card
    // behaves as it always has (just that card). Kept unambiguous: membership of the
    // selection, not a modifier, decides which it is.
    const sel = useSelection.getState().ids;
    if (sel.has(id) && sel.size > 1) {
      const groupIds = [...sel];
      const { moved, refused } = await bulkMoveAll(groupIds, status, `Moved to ${STATUS_LABELS[status]}`);
      if (refused.length) {
        useSelection.getState().only(refused);
        setOnlyIds(new Set(refused));
        showFlash(`${moved.length} moved · ${refused.length} couldn’t — ${refused.length === 1 ? 'it’s' : 'they’re'} blocked`);
      } else {
        clearSel();
        showFlash(`${moved.length} card${moved.length === 1 ? '' : 's'} moved to ${STATUS_LABELS[status]}`);
      }
      return;
    }
    const issue = issues.find((i) => i.id === id);
    if (status === 'done' && issue && !doneAllowed(issue, meta)) {
      // A fully-ticked card checklist clears the gate in place of the board DoD
      // (doneAllowed); otherwise the outstanding checks are named in the refusal.
      setRefused({ ref: issue.ref, missing: outstandingChecks(issue, meta) });
      return;
    }
    setRefused(null);
    // THE SOFT START-GATE — starting a card that still waits on an unfinished
    // predecessor warns, it does not refuse. Cancel leaves the card where it is;
    // "Start anyway" moves it and records the override on its history.
    if (issue) {
      const byRef = new Map(issues.map((c) => [c.ref, c] as const));
      const gate = await askStartGate(issue, status, byRef, displayName(myEmail, profiles) || myEmail, meta.depHardBlock ?? false);
      if (!gate.proceed) return; // the card does not move
      void moveIssue(id, status, gate.overrideNote ? { overrideNote: gate.overrideNote } : undefined);
    } else {
      void moveIssue(id, status);
    }
    maybeAskDoer(status, id);
  };

  // Priority has always been the order, and stays the tiebreak for the others so
  // that within a due date or an age the more urgent card still leads.
  const byPriority = (a: Issue, b: Issue) =>
    PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority) ||
    b.updatedAt - a.updatedAt;
  const comparators: Record<BoardSort, (a: Issue, b: Issue) => number> = {
    priority: byPriority,
    updated: (a, b) => b.updatedAt - a.updatedAt,
    // Soonest due first; a card with no due date sinks below those that have one.
    due: (a, b) => {
      const da = a.dueDate ? Date.parse(a.dueDate) : Infinity;
      const db = b.dueDate ? Date.parse(b.dueDate) : Infinity;
      return da - db || byPriority(a, b);
    },
    // Oldest in flight first; a card not yet started has no age and sinks last.
    age: (a, b) => (a.startedAt ?? Infinity) - (b.startedAt ?? Infinity) || byPriority(a, b),
  };
  const sortCards = (rows: Issue[]) => [...rows].sort(comparators[sortBy]);

  /**
   * One board column, for a given set of rows — the plain board passes the whole
   * board, a swimlane passes only its own cards. `keyPrefix` scopes the
   * drop-highlight so a drag lights up just this column, not every lane's column
   * of the same status. The + to add a card only shows on the plain board: a
   * card added inside a lane would not inherit that lane's owner or phase, so it
   * would jump to another lane on save, which reads as the board losing it.
   */
  const column = (status: IssueStatus, rows: Issue[], keyPrefix: string, showAdd: boolean) => {
    const all = sortCards(rows.filter((i) => i.status === status));
    const isDone = status === 'done';
    const isBacklog = status === 'backlog';
    const cards = isDone && !showAllDone
      ? all.slice(0, DONE_PREVIEW)
      : isBacklog
        ? all.slice(0, BACKLOG_PREVIEW)
        : all;
    const hidden = all.length - cards.length;
    const zone = `${keyPrefix}:${status}`;
    return (
      <div
        key={zone}
        className={`column col-${status} ${dragOver === zone ? 'drag-over' : ''}`}
        data-status={status}
        data-count={all.length}
        data-label={STATUS_LABELS[status]}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(zone);
        }}
        onDragLeave={() => setDragOver(null)}
        onDrop={drop(status)}
      >
        <div className="col-head">
          <span className={`status-dot dot-${status}`} />
          {STATUS_LABELS[status]} <span className="count">{all.length}</span>
          {showAdd && (
            <button
              {...inert('col-add', () => startCreate(status), `New issue in ${STATUS_LABELS[status]}`)}
              aria-label={`New issue in ${STATUS_LABELS[status]}`}
            >
              <Icon name="plus" size={13} />
            </button>
          )}
          {/* Select every card in this column — one tick to pick the whole lane
              (project-bulk-card-changes). Shown ONLY in select mode, at the right of
              the header; a ticked box when all are already selected. */}
          {all.length > 0 && selectMode && (() => {
            const colIds = all.map((i) => i.id);
            const allSel = colIds.every((cid) => selIds.has(cid));
            return (
              <span
                className={`col-selectall${allSel ? ' on' : ''}`}
                role="checkbox"
                aria-checked={allSel}
                tabIndex={0}
                aria-label={allSel ? `Deselect all in ${STATUS_LABELS[status]}` : `Select all in ${STATUS_LABELS[status]}`}
                title={allSel ? 'Deselect all in this column' : 'Select all in this column'}
                onClick={() => (allSel ? removeSel(colIds) : addSel(colIds))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    allSel ? removeSel(colIds) : addSel(colIds);
                  }
                }}
              >
                <svg className="ic" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
              </span>
            );
          })()}
        </div>
        {/* The backlog column states its own boundary in plain words — it is a holding
            area, not open work — so the "backlog ≠ open items" rule is on screen, not
            just in the counts. */}
        {isBacklog && <p className="col-queue-note">Queued — not counted as open work</p>}
        <div className="col-cards">
          {cards.map((i) => (
            <IssueCard key={i.id} issue={i} />
          ))}
          {cards.length === 0 && (() => {
            const heading = EMPTY_COL_HEADING[status] ?? 'All caught up';
            // A custom heading already names the empty state, so it stands alone;
            // the active lanes keep their body line under "All caught up".
            const body = EMPTY_COL_HEADING[status] ? '' : EMPTY_COLUMN[status];
            const drag = canMove ? EMPTY_COL_DRAG[status] ?? 'Drag something here to start it.' : '';
            const detail = [body, drag].filter(Boolean).join(' ');
            return (
              <div className="empty-col">
                <strong>{heading}</strong>
                {detail && <span>{detail}</span>}
              </div>
            );
          })()}
          {hidden > 0 && isBacklog && (
            <button className="col-more" onClick={() => setView('backlog')}>
              Show all {all.length} in backlog <Icon name="chevron" size={14} />
            </button>
          )}
          {hidden > 0 && !isBacklog && (
            <button className="col-more" onClick={() => setShowAllDone(true)}>
              Show {hidden} more finished
            </button>
          )}
          {isDone && showAllDone && all.length > DONE_PREVIEW && (
            <button className="col-more" onClick={() => setShowAllDone(false)}>
              Collapse finished work
            </button>
          )}
        </div>
      </div>
    );
  };

  /**
   * The horizontal lanes the board is split into. "none" is one unlabelled lane
   * — the ordinary board. Otherwise one lane per assignee or per phase, built
   * from the cards actually on the board so no empty lanes are drawn, with the
   * no-value bucket (Unassigned / No phase yet) always last.
   */
  type Lane = { key: string; label: string; rows: Issue[]; tint?: string; done?: boolean };
  // An epic lane is "done" when it holds cards and every one of them has reached
  // the Done column — a fully-finished epic, which we sink to the bottom of the
  // board so it stops taking space above work still in flight (Vinnie, 4 Nov).
  const laneAllDone = (rows: Issue[]) => rows.length > 0 && rows.every((r) => r.status === 'done');
  // "Show it" narrows the board to the refused cards from a partial move, until
  // "Show all" clears it (project-bulk-card-changes).
  const onlyFilter = (list: Issue[]) => (onlyIds ? list.filter((i) => onlyIds.has(i.id)) : list);
  const boardCards = onlyFilter(shown).filter((i) => cols.includes(i.status));
  const lanes: Lane[] = (() => {
    if (groupBy === 'assignee') {
      const byKey = new Map<string, Issue[]>();
      for (const i of boardCards) {
        const k = (i.assignee || '').trim();
        (byKey.get(k) ?? byKey.set(k, []).get(k)!).push(i);
      }
      // Whoever the card is assigned to, named the way the rest of the app names
      // them — a person by their profile name, an assistant by its own name
      // rather than the raw "assistant:codey" handle.
      const nameOf = (k: string) => assigneeName(k, meta, (e) => displayName(e, profiles));
      const named = [...byKey.keys()].filter((k) => k !== '');
      named.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
      const out: Lane[] = named.map((k) => ({ key: k, label: nameOf(k), rows: byKey.get(k)! }));
      const none = byKey.get('');
      if (none) out.push({ key: '(unassigned)', label: 'Unassigned', rows: none });
      return out;
    }
    if (groupBy === 'responsible') {
      // One lane per Responsible, the mirror of the Accountable lanes above
      // — same naming, an assistant named by its own name. Cards with no
      // Responsible named yet gather under "Nobody".
      const byKey = new Map<string, Issue[]>();
      for (const i of boardCards) {
        const k = (i.responsible || '').trim();
        (byKey.get(k) ?? byKey.set(k, []).get(k)!).push(i);
      }
      const nameOf = (k: string) => assigneeName(k, meta, (e) => displayName(e, profiles));
      const named = [...byKey.keys()].filter((k) => k !== '');
      named.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
      const out: Lane[] = named.map((k) => ({ key: k, label: nameOf(k), rows: byKey.get(k)! }));
      const none = byKey.get('');
      if (none) out.push({ key: '(no-doer)', label: 'Nobody', rows: none });
      return out;
    }
    if (groupBy === 'phase') {
      // The same ordering the Backlog's group-by-phase uses: live phases in plan
      // order, then retired ones, keyed by id so a rename cannot merge two — with
      // a "No phase yet" bucket last for the unphased and the unknown.
      const known = new Set(phases.map((p) => p.id));
      const ordered = [...livePhases(phases), ...phases.filter((p) => p.retiredAt)];
      const out: Lane[] = [];
      for (const p of ordered) {
        const rows = boardCards.filter((i) => i.phase === p.id);
        // A finished phase is tagged "Complete" and muted, but it KEEPS its place
        // in the plan sequence — phases are the one grouping that is ordered and
        // dated, so sinking a done early phase below a later one would scramble the
        // timeline. Mark it in place rather than move it (Vinnie, 4 Nov).
        if (rows.length) out.push({ key: p.id, label: p.retiredAt ? `${p.name} (retired)` : p.name, rows, done: laneAllDone(rows) });
      }
      const none = boardCards.filter((i) => !i.phase || !known.has(i.phase));
      if (none.length) out.push({ key: '(no phase)', label: 'No phase yet', rows: none });
      return out;
    }
    if (groupBy === 'epic') {
      // A lane per epic, alphabetical, each wearing its own colour (the same tint
      // the card chip uses, hashed from the name so it is stable everywhere) — with
      // an unepic'd "No epic" bucket last, like the other groupings.
      const byKey = new Map<string, Issue[]>();
      for (const i of boardCards) {
        const k = (i.epic || '').trim();
        (byKey.get(k) ?? byKey.set(k, []).get(k)!).push(i);
      }
      const named = [...byKey.keys()].filter((k) => k !== '').sort((a, b) => a.localeCompare(b));
      const mk = (k: string): Lane => ({
        key: k,
        label: k,
        rows: byKey.get(k)!,
        tint: epicBackground(k, named),
        done: laneAllDone(byKey.get(k)!),
      });
      // Epics still in flight stay up top (alphabetical). Fully-finished epics sink
      // to the very bottom — below the "No epic" bucket, which may still hold active
      // work — so completed work never pushes down the work being done.
      const out: Lane[] = named.filter((k) => !laneAllDone(byKey.get(k)!)).map(mk);
      const none = byKey.get('');
      if (none) out.push({ key: '(no epic)', label: 'No epic', rows: none });
      out.push(...named.filter((k) => laneAllDone(byKey.get(k)!)).map(mk));
      return out;
    }
    if (groupBy === 'area') {
      // A lane per area, keyed by the area's id so two areas that share a name
      // cannot merge and a retired one still draws — the card keeps the area it
      // was given. Each lane wears the area's OWN stored colour, the same dot the
      // card rail and the Backlog use, alphabetical by name, with a "No area yet"
      // bucket last like the other groupings.
      const byKey = new Map<string, Issue[]>();
      for (const i of boardCards) {
        const a = areas.find((x) => x.id === i.area);
        const k = a ? a.id : '';
        (byKey.get(k) ?? byKey.set(k, []).get(k)!).push(i);
      }
      const areaById = (id: string) => areas.find((a) => a.id === id);
      const named = [...byKey.keys()]
        .filter((k) => k !== '')
        .sort((a, b) => (areaById(a)?.name ?? '').localeCompare(areaById(b)?.name ?? ''));
      const mk = (k: string): Lane => {
        const a = areaById(k)!;
        return {
          key: k,
          label: a.retiredAt ? `${a.name} (retired)` : a.name,
          rows: byKey.get(k)!,
          tint: a.colour,
          done: laneAllDone(byKey.get(k)!),
        };
      };
      // An Area is an unordered classification, like an Epic — so a fully-finished
      // area sinks to the bottom (below the No-area bucket), tagged Complete, while
      // the active areas stay up top in name order (Vinnie, 4 Nov).
      const out: Lane[] = named.filter((k) => !laneAllDone(byKey.get(k)!)).map(mk);
      const none = byKey.get('');
      if (none) out.push({ key: '(no area)', label: 'No area yet', rows: none });
      out.push(...named.filter((k) => laneAllDone(byKey.get(k)!)).map(mk));
      return out;
    }
    if (groupBy === 'label') {
      // THE ONE MULTI-VALUE GROUPING: a card carries any number of labels, so it
      // appears in EACH of its labels' lanes — a lane's count is therefore not a
      // card count, which the banner above the board says in words. Lanes run in
      // the registry's live order, then any label a card carries that is not in the
      // registry (or retired), then a "No label" bucket for the untagged — the same
      // "no value still needs a home" rule the other groupings keep.
      const used = new Set<string>();
      for (const i of boardCards) for (const l of i.labels ?? []) used.add(l);
      const liveNames = liveLabels(labelReg).map((l) => l.name).filter((n) => used.has(n));
      const extra = [...used]
        .filter((n) => !liveLabels(labelReg).some((l) => l.name === n))
        .sort((a, b) => a.localeCompare(b));
      const out: Lane[] = [...liveNames, ...extra].map((name) => ({
        key: `label:${name}`,
        label: name,
        rows: boardCards.filter((i) => (i.labels ?? []).includes(name)),
        tint: labelColour(name, labelReg),
      }));
      const none = boardCards.filter((i) => (i.labels?.length ?? 0) === 0);
      if (none.length) out.push({ key: '(no label)', label: 'No label', rows: none });
      return out;
    }
    return [{ key: 'all', label: '', rows: shown }];
  })();

  /* The one lane the board is focused on, when grouped and a value is chosen.
     Undefined means the ordinary all-lanes view. If the chosen lane no longer has
     cards on the board it is gone, and this falls back to all rather than an empty
     board. */
  const focusLane = groupBy !== 'none' && focus ? lanes.find((l) => l.key === focus) : undefined;

  /* The marker a lane wears in the focus control — a person's initials avatar
     under Assignee, the epic's or area's own colour dot under Epic/Area, nothing
     under Phase (its lanes carry no colour, so the name stands alone). Within one
     grouping every row wears the same kind, so the list stays aligned. */
  const laneMark = (lane: Lane) =>
    groupBy === 'assignee' || groupBy === 'responsible' ? (
      <span className="board-focus-av" aria-hidden="true">{initials(lane.label)}</span>
    ) : groupBy === 'epic' || groupBy === 'area' || groupBy === 'label' ? (
      <span
        className="board-focus-dot"
        style={lane.tint ? ({ '--dot': lane.tint } as unknown as CSSProperties) : undefined}
        aria-hidden="true"
      />
    ) : null;

  /*
   * FOLDING A SWIMLANE TO ITS HEADER — per lane, and a one-press Collapse all.
   *
   * On a board grouped into a dozen lanes, reading one person's or one epic's
   * load means scrolling past all the others. Collapsing a lane leaves just its
   * header — the name and its count — so a busy board flattens to a scannable
   * index of who/what and how much, and opens back out a lane at a time.
   *
   * Kept per person, per board, in this browser, like group-by and sort above:
   * which lanes somebody has folded away is their view, not a shared setting.
   * Keyed by lane key; a key from another grouping simply matches no lane here,
   * so switching grouping never folds the wrong lane — it just shows them open.
   */
  const collapseKey = `flightdeck-board-collapsed-${projectId}`;
  const [collapsedLanes, setCollapsedLanes] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(collapseKey);
      const parsed = raw ? (JSON.parse(raw) as { lanes?: unknown }) : null;
      return new Set(Array.isArray(parsed?.lanes) ? (parsed!.lanes as string[]) : []);
    } catch {
      return new Set();
    }
  });
  const rememberCollapsed = (next: Set<string>) => {
    try {
      localStorage.setItem(collapseKey, JSON.stringify({ lanes: [...next] }));
    } catch {
      /* private browsing — the fold still applies, it just forgets next load */
    }
  };
  const toggleLane = (key: string) =>
    setCollapsedLanes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      rememberCollapsed(next);
      return next;
    });
  // Collapse all / Expand all reads the lanes on screen now: all folded ⇒ the
  // button offers to open them; otherwise it offers to fold them.
  const allCollapsed = lanes.length > 0 && lanes.every((l) => collapsedLanes.has(l.key));
  const foldAll = () => {
    const next = allCollapsed ? new Set<string>() : new Set(lanes.map((l) => l.key));
    setCollapsedLanes(next);
    rememberCollapsed(next);
  };
  // The Sprint picker and the collapse controls only make sense on a board that
  // actually works in sprints / is split into lanes.
  const sprintable = meta.cadence === 'sprints' && sprints.length > 0;
  const grouped = groupBy !== 'none' && !focusLane && lanes.length > 0;

  /* DEMOTE, DON'T WRAP — decide it by MEASUREMENT, not a fixed width, because the full
     toolbar needs far more room when the board is grouped (it also carries the focus
     picker and Collapse-all). While the full layout is on screen we measure the width it
     needs on one line — the sum of its controls' natural widths plus the gaps between
     them — and demote to the compact form if that exceeds the room. (We sum widths rather
     than read scrollWidth under nowrap, because the segmented control would SHRINK under
     nowrap and hide the true need; a wrapped row keeps every control at its natural size.)
     While compact, we return to the full form only once there is clearly room again (a
     small buffer stops it flapping), and the whole thing is re-measured whenever the
     grouping — and so the control set — changes. Desktop reflow only; no media query, and
     no feedback loop (the measurement reads the full layout's need, never the compact
     row's own width). */
  useEffect(() => {
    const el = toolsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measureNeed = () => {
      const cs = getComputedStyle(el);
      const gap = parseFloat(cs.columnGap) || 0;
      const kids = Array.from(el.children).filter((c) => getComputedStyle(c).display !== 'none');
      let need = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      kids.forEach((c) => { need += c.getBoundingClientRect().width; });
      return need + gap * Math.max(0, kids.length - 1);
    };
    const evaluate = () => {
      const avail = el.clientWidth;
      if (avail === 0) return; // not laid out yet
      if (!el.classList.contains('is-tight')) {
        wideNeed.current = measureNeed();
        if (wideNeed.current > avail + 1) setTight(true);
      } else if (wideNeed.current && avail > wideNeed.current + 24) {
        setTight(false);
      }
    };
    // Re-measure from the full layout on (re)mount and whenever grouping changes. Two
    // frames so the full layout has painted before we sum its controls.
    wideNeed.current = 0;
    setTight(false);
    let r1 = 0;
    let r2 = 0;
    r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(evaluate); });
    const ro = new ResizeObserver(evaluate);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      ro.disconnect();
    };
  }, [grouped, sprintable]);

  /**
   * The single thing holding the most work back.
   *
   * Shown whenever there IS one — not only when the board has nothing ready.
   * It used to appear only in that second case, on the reasoning that "what do
   * I start" is the better question while anything is startable. But a blocker
   * holding three issues is worth seeing on a day when one thing happens to be
   * ready, and hiding it until the board seizes up is how it stays hidden.
   */
  // The leverage lead (title + the "unblocks N" sub) now rides the slim Next Up
  // bar below. What was the leverage card is split so the bar stays one line:
  //   • `leverageSub`    — the one-line "unblocks N" summary shown beside the title;
  //   • `leverageActions`— its default buttons, shown inline in the bar until you act;
  //   • `confirmPanel`   — the resolve/confirm flow, which glides open UNDER the bar
  //                        in the shared Expander (the same open/close motion as the
  //                        rest of the app).
  // Same logic and copy as before, only relocated. An exact group names what
  // clearing it releases; a category keeps the wording that stays true.
  const leverageSub = top
    ? top.exact
      ? (<>unblocks <b>{downstreamCount(top.issues.length)}</b></>)
      : unblockEffect(top.exact, top.issues.length, top.detail)
    : null;
  /* THIS is where resolving a blocker belongs. Assigning and opening act on the
     BLOCKER — the card named in the title — not the items waiting behind it; where
     the blocker is not a card at all, those two drop. The confirm step is asked
     and checked inline, under the bar, with what the board found printed beside it,
     rather than a contextless browser dialog. */
  const confirmPanel = top && confirming ? (
        decisionClear ? (
          /* A DECISION blocker — the board was waiting on a call, not on work to
             finish. Clearing it is the moment to capture what was decided, exactly
             as the card does, so it lands in the decision log instead of being
             dropped. The primary logs it AND clears; "Just clear" is the escape
             hatch. One decision, linked to every card that was waiting. */
          <div className="resolve-confirm is-backed">
            <div className="seed-h">
              <Icon name="check" size={16} />
              What was decided?
            </div>
            <p className="resolve-effect">
              Logging it clears the blocker from{' '}
              <b>
                {top.issues.length} card{top.issues.length === 1 ? '' : 's'}
              </b>{' '}
              ({top.issues.map((i) => i.ref).join(', ')}) and files the decision in the log.
            </p>
            {decisionReasons.length > 0 && (
              <>
                <div className="seed-prefill">What the board was waiting on</div>
                {decisionReasonsDistinct.length <= 1 ? (
                  <div className="seed-reason">{decisionReasonsDistinct[0]}</div>
                ) : (
                  <div className="seed-reason">
                    {decisionReasons.map((r) => (
                      <div key={r.ref}>
                        <b>{r.ref}</b> — {r.text}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <input
              className="ctl"
              style={{ marginTop: 10 }}
              aria-label="What was decided"
              placeholder="Type the decision that was made"
              value={decidedText}
              onChange={(e) => setDecidedText(e.target.value)}
            />
            <div className="leverage-actions" style={{ marginTop: 10 }}>
              <button
                className="btn btn-primary"
                disabled={seedLogging}
                onClick={async () => {
                  setSeedLogging(true);
                  setSeedProblem('');
                  const err = await logDecision(
                    decisionFromBlockerGroup(
                      top.issues.map((i) => ({ id: i.id, ref: i.ref, title: i.title })),
                      decidedText.trim()
                    )
                  );
                  setSeedLogging(false);
                  // Clear only once the decision is safely logged, so the blocker
                  // never vanishes with nothing recorded.
                  if (err) {
                    setSeedProblem(err);
                    return;
                  }
                  clearLeverageBlocker(top.issues);
                }}
              >
                <Icon name="check" size={14} /> Clear blocker &amp; log decision
              </button>
              <button className="btn" disabled={seedLogging} onClick={() => clearLeverageBlocker(top.issues)}>
                Just clear the blocker
              </button>
              <button className="btn" disabled={seedLogging} onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </div>
            {seedProblem && (
              <p className="hintline" role="alert" style={{ marginTop: 8 }}>
                {seedProblem}
              </p>
            )}
          </div>
        ) : (
        <div className={`resolve-confirm ${resolveIsBacked(verdict) ? 'is-backed' : 'is-unbacked'}`}>
          <p className="resolve-question">Has this work been done?</p>
          <p className="resolve-found">
            <span className="resolve-mark" aria-hidden="true">
              {resolveIsBacked(verdict) ? '✓' : '!'}
            </span>
            {resolveMessage(verdict)}
          </p>
          <p className="resolve-effect">
            Clearing it removes the blocker from{' '}
            <b>
              {top.issues.length} issue{top.issues.length === 1 ? '' : 's'}
            </b>{' '}
            ({top.issues.map((i) => i.ref).join(', ')}), including the written reason on each.
          </p>
          {/* GO AND DO THE WORK, RATHER THAN JUST DENY IT HAPPENED.
              When the board cannot see the work finished, the useful next move
              is not "clear it anyway" — it is opening the card that is holding
              everything up and finishing it there: mark it done, tick its
              checks, write what is left. So that is the primary button, and
              clearing steps aside to a quieter one.
              Once the card DOES show the work done, the position reverses:
              clearing is the whole point and leads. */}
          <div className="leverage-actions">
            {verdict.kind !== 'nothing-to-check' && !resolveIsBacked(verdict) && (
              <button className="btn btn-primary" onClick={() => openIssue(verdict.blocker.id)}>
                Open {verdict.blocker.ref} to finish it
              </button>
            )}
            <button
              className={`btn ${resolveIsBacked(verdict) ? 'btn-primary' : 'btn-danger'}`}
              // One press, one act — clearLeverageBlocker records the group as a
              // single change (the fact that was missing when twenty went at once
              // and the trail showed twenty unconnected edits). This branch is a
              // non-decision blocker, so it clears the scalar fields as before.
              onClick={() => clearLeverageBlocker(top.issues)}
            >
              {resolveIsBacked(verdict) ? 'Yes, clear it' : 'Clear it anyway'}
            </button>
            <button className="btn" onClick={() => setConfirming(false)}>
              Cancel
            </button>
            {/* Verified: the card is settled, but still one click away if you
                want to look before agreeing. */}
            {resolveIsBacked(verdict) && (
              <button className="link" onClick={() => openIssue(verdict.blocker.id)}>
                Open {verdict.blocker.ref}
              </button>
            )}
            {/* No card was named, so there is nowhere to send you to do the
                work — but naming it IS work you can do, and it is what turns
                this into a blocker the board can check next time. */}
            {verdict.kind === 'nothing-to-check' && actOn && (
              <button className="link" onClick={() => openIssue(actOn.id)}>
                Open {actOn.ref} to say what it is waiting on
              </button>
            )}
          </div>
        </div>
        )
  ) : null;

  // The bar's default actions — what to do about the bottleneck before any confirm
  // step. btn-small so they sit on the one-line strip. Resolve opens the confirm
  // flow (which glides in above the bar); the rest act on the blocker card itself.
  const leverageActions = top && !confirming ? (
    <>
          {canEdit && mineToEdit('blocked') && (
            <button
              {...inert('btn btn-primary btn-small', () => {
                // The "what was decided?" field opens EMPTY — the blocker's reason
                // is shown read-only above it, so the outcome is typed fresh rather
                // than the open question being logged as the decision by a click-through.
                setDecidedText('');
                setConfirming(true);
              })}
            >
              Resolve blocker
            </button>
          )}
          {actOn && (
            <>
              {canEdit && myEmail && actOn.responsible?.toLowerCase() !== myEmail && (
                /* Taking a card means becoming its DOER (Responsible) now, not its
                   Accountable owner — the two-part assignee re-point (A). */
                <button {...inert('btn btn-small', () => updateIssue(actOn.id, { responsible: myEmail }))}>
                  I&rsquo;ll do this
                </button>
              )}
              <button className="btn btn-small" onClick={() => openIssue(actOn.id)}>
                Open {actOn.ref}
              </button>
            </>
          )}
    </>
  ) : null;

  /* `board-page` only caps the width. The board used to run to the window while
     every other screen stopped somewhere, and the width it now shares with them
     is set by this screen's own floor — its four columns cannot go below 1108px
     between them without overflowing. */
  // WHEELS UP (easter egg #1) — read-only completeness signal. Counted over the
  // REAL cards (not the search-filtered `visible`, so a search cannot fake a
  // finished board) and only the WORK columns — the backlog is never part of
  // completeness, even when it is shown as a column (0218), or a board with a
  // non-empty queue could never read finished. It never reads or writes the epic
  // roll-up or the progress computation.
  const onBoardNow = issues.filter((i) => i.status !== 'backlog' && cols.includes(i.status));
  const doneNow = onBoardNow.filter((i) => i.status === 'done');
  const boardComplete = doneNow.length > 0 && doneNow.length === onBoardNow.length;
  const doneSignature = boardComplete ? doneNow.map((i) => i.id).sort().join(',') : '';

  // The doer choices the prompt offers — the SAME set the card's Responsible
  // field builds: everyone who can be assigned on this board, the board's
  // assistants, "Unassigned", and the current doer if it is somehow off-list.
  const doerOptions = useMemo<PickOption[]>(() => {
    if (!doerAsk) return [];
    const people = boardAssignees(
      projects.find((p) => p.id === projectId),
      access.emails,
      access.admins,
      groupsOf
    );
    const current = doerAsk.responsible ?? '';
    return [
      { value: '', label: 'Unassigned' },
      ...people.map((m) => ({ value: m, label: displayName(m, profiles), trailing: <VendorTag who={m} profiles={profiles} compact /> })),
      ...assistantsOf(meta).map((a) => ({
        value: assistantAssignee(a.id),
        label: a.name?.trim() || a.id,
        group: 'Assistants',
      })),
      ...(current && !people.includes(current) ? [{ value: current, label: current, trailing: <VendorTag who={current} profiles={profiles} compact /> }] : []),
    ];
  }, [doerAsk, projects, projectId, access, groupsOf, profiles, meta]);

  // Record that we have asked for this card + mover, so a dismissal — or a
  // choice — is never followed by a second nag, then close the prompt.
  const closeDoerAsk = () => {
    if (doerAsk && myEmail) dismissedDoer.current.add(`${doerAsk.id}|${myEmail}`);
    setDoerAsk(null);
  };

  // ── Bulk selection order + lifecycle (project-bulk-card-changes) ─────────────
  // The flat visual order the selection store measures a Shift-range against —
  // column-major (status by status, each column top-to-bottom), the reading order
  // of the ordinary board. The Grid will feed its own order the same way.
  const selOrder = useMemo(
    () => cols.flatMap((s) => sortCards(boardCards.filter((i) => i.status === s))).map((i) => i.id),
    // sortCards closes over sortBy; list by the inputs that change the order.
    [cols, boardCards, sortBy], // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => {
    // In grid mode the Grid feeds its own displayed order (sorted + filtered);
    // this kanban column-major order must not clobber it, or a Shift-range in the
    // Grid would measure against the wrong order and miss rows.
    if (boardMode === 'grid') return;
    setSelOrder(selOrder);
  }, [selOrder, setSelOrder, boardMode]);
  // Leaving the board drops the selection and any "Show it" filter — a tick made
  // here must not haunt the next board, where those ids mean nothing. Leaving also
  // ends select mode, so the next board opens in its ordinary (OFF) state.
  useEffect(() => {
    exitSelect();
    setOnlyIds(null);
  }, [projectId, exitSelect]);

  // THE `S` KEY toggles select mode, and ESCAPE leaves it — the keyboard twins of
  // the toolbar toggle. Both ignore keystrokes aimed at a text field or an open
  // menu / dialog, which own those keys themselves. Escape exits only while in
  // select mode, so it never fights the many other Escapes on the board.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.key === 's' || e.key === 'S') && !e.repeat) {
        e.preventDefault();
        toggleMode();
      } else if (e.key === 'Escape' && useSelection.getState().selectMode) {
        // A verb menu, the result toast or a dialog owns Escape first; only an
        // otherwise-idle Escape leaves select mode.
        if (document.querySelector('.bulk-menu, .bulk-toast, [role="dialog"], .modal')) return;
        exitSelect();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleMode, exitSelect]);

  // A click on EMPTY board space leaves select mode (so nothing lingers). This is a
  // NEGATIVE test: anything that is a card, a control, a menu, the toolbar or a
  // dialog is that thing's own click and bails; EVERYTHING ELSE on the board (the
  // columns wrapper, a lane, the gaps between and below cards, the page margins) is
  // empty space and exits. A positive allow-list missed the real empty containers.
  const onBoardBackgroundClick = (e: React.MouseEvent) => {
    if (!useSelection.getState().selectMode) return;
    const t = e.target as HTMLElement;
    if (
      t.closest(
        '.card, .gv-row, .gv-thead, .col-head, .board-tools, .board-modes, .bulk-dock, .bulk-menu, .doer-pop, ' +
          'button, a, input, select, textarea, label, [role="button"], [role="checkbox"], ' +
          '[role="menu"], [role="menuitem"], [role="menuitemradio"], [role="dialog"], .modal',
      )
    )
      return;
    exitSelect();
  };

  return (
    <AttentionChipsContext.Provider value={attnChipByRef}>
    <div
      className={`board-page${boardMode === 'grid' ? ' is-grid' : ''}${selIds.size > 0 ? ' has-bulkbar' : ''}`}
      onClick={onBoardBackgroundClick}
    >
      {doerAsk && (
        <DoerPrompt
          issueId={doerAsk.id}
          issueRef={doerAsk.ref}
          options={doerOptions}
          /* Put the Accountable person forward as the likely doer (Vinnie,
             23 Oct) — highlighted, one Enter away, but not pre-committed. */
          suggest={doerAsk.assignee ?? ''}
          onPickMe={() => {
            updateIssue(doerAsk.id, { responsible: myEmail });
            closeDoerAsk();
          }}
          onPick={(who) => {
            updateIssue(doerAsk.id, { responsible: who });
            closeDoerAsk();
          }}
          onDismiss={closeDoerAsk}
        />
      )}
      {/* THE GO-LIVE -> IN-SERVICE HANDOFF (#435 surface 2). Self-gating and
          portalled: it paints only when this project looks ready to hand over, and
          otherwise renders nothing. Mounted here so it rides with the board. */}
      <HandoffPrompt />

      {/* BOARD ⇄ GRID — the same board, two layouts (#508). Shown in both, so the
          way back is always in the same place; the choice is kept per board in
          this browser (see setMode above). */}
      <div className="board-modes" role="group" aria-label="Board layout">
        <div className="seg">
          <button
            type="button"
            className={`seg-btn${boardMode === 'kanban' ? ' active' : ''}`}
            aria-pressed={boardMode === 'kanban'}
            onClick={() => setMode('kanban')}
          >
            <Icon name="board" size={15} />
            Board
          </button>
          <button
            type="button"
            className={`seg-btn${boardMode === 'grid' ? ' active' : ''}`}
            aria-pressed={boardMode === 'grid'}
            onClick={() => setMode('grid')}
          >
            <Icon name="table" size={15} />
            Grid
          </button>
          {/* Calendar — the schedule on a calendar. Every board gets it: a
              continuous board opens on the Month grid (cards sit on their dates).
              The Gantt Timeline inside it is the project-only half — the whole
              critical-path story gates to project mode — so CalendarView shows the
              Timeline sub-view only when the board is a project. */}
          <button
            type="button"
            className={`seg-btn${boardMode === 'calendar' ? ' active' : ''}`}
            aria-pressed={boardMode === 'calendar'}
            onClick={() => setMode('calendar')}
          >
            <Icon name="calendar" size={15} />
            Calendar
          </button>
        </div>
        {/* NEEDS ATTENTION — the board-wide "what to act on" list (Metrics Layer A).
            The pill appears only when the board has something flagged, carries the
            live count, and toggles the grouped list in place of the board body. */}
        {attnResult.counts.total > 0 && (
          <button
            type="button"
            className={`attn-pill has${showAttention ? ' on' : ''}`}
            aria-pressed={showAttention}
            onClick={() => setShowAttention((v) => !v)}
          >
            <Icon name="warning" size={15} />
            Needs Attention
            <span className="n">{attnResult.counts.total}</span>
          </button>
        )}
      </div>

      {/* IN SERVICE — once the project has been handed over, the board reads as a
          live service here (pill + "Live since" + owner), with the one-time success
          ribbon the moment it flips (#435 surface 2). Renders nothing otherwise. */}
      <InServiceHeader />

      {/* PRE-HANDOFF — a bounded delivery board that is eligible to go live carries
          a persistent "Mark it live" ribbon (amber at go-live, red after 14 days),
          so the handoff is never a one-time prompt a person has to chase (#435,
          Vinnie 22 Oct). Renders nothing once in service or before eligibility. */}
      <GoLiveRibbon />

      {showAttention ? (
        <NeedsAttentionPanel
          result={attnResult}
          issueByRef={issueByRef}
          sprintNameById={sprintName}
          muted={attnMuted}
          onToggleMute={toggleAttnMute}
          onOpenCard={openCardByRef}
          onClose={() => setShowAttention(false)}
        />
      ) : boardMode === 'calendar' ? (
        <CalendarView />
      ) : boardMode === 'grid' ? (
        <GridView />
      ) : (
      <>
      {/* PROGRESS AT THE TOP — the board-metrics strip (mock B), replacing the
          condensed plan-guide strip. A board run without a project reads its
          progress here (derived from card status + timestamps); a project-run
          board folds it and works in the Plan. It sits above everything, with
          quick-add just beneath it (Vinnie, 24 Sep 2026). */}
      <BoardMetricsStrip />

      {/* FIRST RUN — an empty board is where a newcomer is most lost, so it leads
          with a plain welcome and the setup guide (the learnability-witness F4/F7:
          the empty board had no "start here", and the guide only ever showed on the
          Plan). Reuses the self-contained ProjectGuide, dismissible via its own
          Hide. Gone the moment the first card lands. Option B welcome, approved
          1 Oct 2026. */}
      {issues.length === 0 && (
        <div className="board-firstrun">
          <div className="board-welcome">
            <span className="board-welcome-lead">New to FlightDeck?</span>
            <p className="board-welcome-text">
              {isProject ? (
                <>
                  This board is where the project runs — the columns track the work day to day. The
                  steps below walk you through setting it up end to end, from the charter through to
                  release. Do them in order, or just add a card to get moving.
                </>
              ) : (
                <>
                  This board tracks ongoing work — the columns follow each piece from request through
                  to done. Add a card to get moving, or open <strong>Settings</strong> to tune how the
                  board works.
                </>
              )}
            </p>
            <p className="board-welcome-help">
              Stuck at any point? Open <strong>Help</strong> at the foot of the sidebar, or press{' '}
              <strong>⌘K</strong> and type &ldquo;how do I&hellip;&rdquo; — every answer takes you to the control.
            </p>
          </div>
          {isProject && <ProjectGuide />}
        </div>
      )}

      {/* FAST CARD ENTRY — WS-3. Gated by the same rule as the top-bar New issue:
          a tracker-fed or view-only board does not take cards raised here, so the
          bar is not offered where the save would only refuse it. */}
      {canAdd && !sample.readOnly && (
        <div data-reveal="quick-add">
          <QuickAdd />
        </div>
      )}

      {refused && (
        <div className="refused" role="alert">
          <strong>{refused.ref} is not ready to be done.</strong> Still to do:{' '}
          {refused.missing.join(', ')}.
          <button className="link" onClick={() => setRefused(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* OFFERED, NOT IMPOSED. The questions used to open themselves over the
          whole screen the moment you reached a project nobody had set up —
          including over a card you had just opened. A line you can wave away
          asks the same thing without taking the board off you. */}
      {isProject && setupOutstanding.length > 0 && !setupDismissed && issues.length > 0 && (
        <div className="setup-offer">
          <span>
            This project has not been set up — <b>{setupOutstanding.length} question{setupOutstanding.length === 1 ? '' : 's'}</b> about how
            it should work.
          </span>
          <button className="btn btn-small btn-primary" onClick={startSetup}>
            {setupOutstanding.length === 1 ? 'Answer it' : 'Answer them'}
          </button>
          <button className="link" onClick={dismissSetup}>
            Not now
          </button>
        </div>
      )}

      {/* WHAT IS NOT ON SCREEN, COUNTED — now said at the board, not here. A
          board quietly showing a subset reads as the whole of the work, so the
          filtered state is named right above the columns (the "Viewing …"
          banner below), where the eye is, with the way back one click away. */}

      <section className="nextup">
        {/* THE SLIM NEXT UP BAR. The old tall hero pushed the board columns below
            the laptop fold; it is now one ~48px line — the single highest-leverage
            action inline, the ready-to-start list folded behind a pill — so the
            columns start at the top of the view. The sprint context that used to
            head this block now rides the Sprint control in the board toolbar. */}
        {(top || nextUp.length > 0) && (() => {
          // The bar LEADS with the bottleneck when there is one; with no bottleneck
          // it leads with the first ready item and the pill counts the rest. The
          // list in the drawer is everything the lead has not already named.
          const leadReady = !top && nextUp.length > 0 ? nextUp[0] : null;
          const readyList = top ? nextUp : nextUp.slice(1);
          return (
            <>
              <div className={`nu-bar ${readyOpen ? 'is-open' : ''}`}>
                {top ? (
                  <div className="nu-lead">
                    <span className="nu-spark"><Icon name="bolt" size={14} /></span>
                    <div className="nu-lead-txt">
                      <span className="nu-kicker">Do this next</span>
                      <span className="nu-title">{leverageTitle(top)}</span>
                      {leverageSub && <span className="nu-sub">{leverageSub}</span>}
                    </div>
                  </div>
                ) : leadReady ? (
                  /* No bottleneck — the bar leads with the first ready item. A div,
                     not a button: the whole row opens the card, but its action is a
                     button, and a button inside a button is invalid. */
                  <div
                    className="nu-lead nu-lead-ready"
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${leadReady.ref} — ${leadReady.title}`}
                    onClick={() => openIssue(leadReady.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIssue(leadReady.id); } }}
                  >
                    <div className="nu-lead-txt">
                      <span className="nu-kicker">Start next</span>
                      <span className="bl-ref">{leadReady.ref}</span>
                      <Priority priority={leadReady.priority} />
                      {leadReady.size && <span className="size-chip">{SIZE_LABEL[leadReady.size]}</span>}
                      <span className="nu-title">{leadReady.title}</span>
                      <span className="nu-sub">nothing is blocking it</span>
                    </div>
                  </div>
                ) : null}

                <div className="nu-acts">
                  {top
                    ? leverageActions
                    : leadReady && canEdit && myEmail && leadReady.responsible?.toLowerCase() !== myEmail && (
                        <button {...inert('btn btn-primary btn-small', (e) => { e.stopPropagation(); updateIssue(leadReady.id, { responsible: myEmail }); })}>
                          I&rsquo;ll do this
                        </button>
                      )}
                </div>

                {readyList.length > 0 && (
                  <button
                    className={`nu-ready ${readyOpen ? 'open' : ''}`}
                    aria-expanded={readyOpen}
                    onClick={() => setReadyOpen((o) => !o)}
                  >
                    Ready to start <span className="nu-count">{readyList.length}</span>
                    <span className="nu-chev"><Icon name="chevron" size={16} /></span>
                  </button>
                )}
              </div>

              {/* The resolve/confirm flow glides open UNDER the bar — the shared
                  Expander, the same open/close motion as everywhere else in the app. */}
              {top && (
                <Expander open={confirming}>
                  <div className="nu-confirm">{confirmPanel}</div>
                </Expander>
              )}

              {/* The ready-to-start list — same shared motion, collapsed by default
                  so the board starts at the top. Opening is the whole row; the one
                  secondary action shows on hover and stops the row's own click. */}
              {readyList.length > 0 && (
                <Expander open={readyOpen}>
                  <div className="nu-expand">
                    <div className="nu-expand-h">Ready to start — nothing is blocking these</div>
                    {readyList.slice(0, NEXT_UP_SHOWN).map((i, n) => (
                      <div
                        key={i.id}
                        className="nu-rrow"
                        role="button"
                        tabIndex={0}
                        aria-label={`Open ${i.ref} — ${i.title}`}
                        onClick={() => openIssue(i.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIssue(i.id); } }}
                      >
                        <span className="nu-rank">{n + 1}</span>
                        <span className="bl-ref">{i.ref}</span>
                        <Priority priority={i.priority} />
                        {i.size && <span className="size-chip">{SIZE_LABEL[i.size]}</span>}
                        <span className="nu-rtitle">{i.title}</span>
                        {canEdit && i.responsible?.toLowerCase() !== myEmail && myEmail && (
                          <span className="nu-rdo">
                            <button {...inert('btn btn-small', (e) => { e.stopPropagation(); updateIssue(i.id, { responsible: myEmail }); })}>
                              I&rsquo;ll do this
                            </button>
                          </span>
                        )}
                      </div>
                    ))}
                    {readyList.length > NEXT_UP_SHOWN && (
                      // OPENS THOSE ONES, not the backlog — a count is a promise about
                      // what the next screen contains.
                      <button
                        className="link ready-more"
                        onClick={() => showInBacklog(readyList.slice(NEXT_UP_SHOWN).map((i) => i.id), 'ready to start')}
                      >
                        and {readyList.length - NEXT_UP_SHOWN} more ready to start
                        <Icon name="chevron" size={15} />
                      </button>
                    )}
                  </div>
                </Expander>
              )}
            </>
          );
        })()}

        {/* One line for all of it, opening a list of exactly these issues.
            The backlog strip below is suppressed while this shows, or the same
            stuck work is described twice in two lines that do not add up. */}
        {rest.length > 0 && (
          <p className="nextup-rest">
            {stuckSummary(stuckOnBoard, stuckInBacklog)}.{' '}
            <button className="link" onClick={() => showInBacklog(stuck.map((i) => i.id), 'holding work up')}>
              Show me all {stuck.length}
            </button>
          </p>
        )}
      </section>

      {/* The backlog is now surfaced two always-present ways instead of this once-only
          strip: the toolbar's Backlog chip (always, whenever the queue is non-empty) and
          the optional in-board Backlog column. Both keep it in sight on a busy board,
          where the old strip — gated on an empty board (rest.length === 0) — was hidden
          exactly when the queue mattered most. */}

      {/*
        WHY THE CARDS WILL NOT MOVE, said out loud — but only for the reason that
        has nowhere else to say it. Dragging is switched off for anybody who may
        not move work, so a reviewer pulled a card, watched it spring back, and
        reasonably concluded the board was broken.

        A TRACKER-RUN BOARD says so once, in the sidebar ("Issues are raised in
        TRACKER.md and copied here"), which is where a person forms their idea of
        what this board is. Repeating it in the middle of the columns read like a
        stray line dropped there by accident (Vinnie, 23 Sep) — so the tracker
        case is left to the sidebar and only the role case, which has no other
        home, is explained here.
      */}
      {!canMove && source === 'board' && (
        <p className="hint board-cannot-move">
          You can read and comment here, but moving work between columns is for a{' '}
          <strong>{ROLE_LABEL.member}</strong> and above. You are a{' '}
          <strong>{ROLE_LABEL[role] ?? role}</strong> on this board.
        </p>
      )}

      {/* Group by — lay the board's columns into swimlanes by owner, phase, epic
          or area; and Sort — how the cards within each column are ordered. Both
          reuse the shared controls and are remembered per person, per board. */}
      <div className={`board-tools${tight ? ' is-tight' : ''}`} data-reveal="group-by" ref={toolsRef}>
        {sprintable && (
          <>
            <span className="board-tools-lbl">Sprint</span>
            <div className="board-sprint-wrap" ref={sprintWrap} data-reveal="sprint-pick">
              <button
                className={`tb-btn${sprintOpen ? ' open' : ''}${pickedSprint ? ' is-set' : ''}`}
                aria-haspopup="menu"
                aria-expanded={sprintOpen}
                title="Show one sprint's cards"
                onClick={() => setSprintOpen((o) => !o)}
              >
                <Icon name="calendar" />
                {/* The trigger carries the picked sprint's state, so the toolbar
                    itself says "you are on a finished sprint" — the dot the menu
                    uses, kept when the menu is shut. */}
                {pickedSprint && (
                  <span className={`bs-dot ${pickedSprint.state}`} aria-hidden="true" />
                )}
                <span className="board-focus-cur">
                  {pickedSprint ? pickedSprint.name : 'All Sprints'}
                </span>
                {pickedSprint && (
                  <span
                    className="board-focus-clear"
                    role="button"
                    tabIndex={0}
                    aria-label="Show all sprints again"
                    title="Show all sprints again"
                    onClick={(e) => {
                      e.stopPropagation();
                      pickSprint('');
                    }}
                  >
                    ✕
                  </span>
                )}
              </button>
              {sprintOpen && (
                <div className="board-sprint-menu" role="menu" aria-label="Show one sprint's cards">
                  {/* Type-to-filter, so 20+ finished sprints stay scannable and
                      the menu is keyboard-first (the same input the Focus menu uses). */}
                  <div className="board-sprint-find">
                    <input
                      className="board-focus-look"
                      value={sprintQuery}
                      onChange={(e) => setSprintQuery(e.target.value)}
                      placeholder="Find a sprint…"
                      aria-label="Find a sprint"
                      autoFocus
                    />
                  </div>
                  <button
                    role="menuitemradio"
                    aria-checked={!sprintPick}
                    className={`board-sprint-opt${!sprintPick ? ' on' : ''}`}
                    onClick={() => {
                      pickSprint('');
                      setSprintOpen(false);
                    }}
                  >
                    <span className="board-sprint-tick" aria-hidden="true">{!sprintPick ? '✓' : ''}</span>
                    <span className="board-focus-av all" aria-hidden="true">
                      <Icon name="board" size={12} />
                    </span>
                    <span className="board-sprint-nm">All Sprints</span>
                    <span className="count">{onTheBoard(visible).length}</span>
                  </button>
                  <div className="board-focus-sep" role="separator" />
                  {(() => {
                    const q = sprintQuery.trim().toLowerCase();
                    const match = (s: Sprint) => !q || s.name.toLowerCase().includes(q);
                    // Group headings are the product's own words for a sprint's
                    // state (Running / Planned / Finished), not a second vocabulary.
                    const groups: { head: string; items: Sprint[] }[] = [
                      { head: SPRINT_STATE_LABEL.active, items: sprints.filter((s) => s.state === 'active' && match(s)) },
                      { head: SPRINT_STATE_LABEL.planned, items: sprintsInOrder(sprints.filter((s) => s.state === 'planned' && match(s))) },
                      { head: SPRINT_STATE_LABEL.closed, items: sprintsInOrder(sprints.filter((s) => s.state === 'closed' && match(s))) },
                    ];
                    const metaOf = (s: Sprint) =>
                      s.state === 'active'
                        ? `${timeLeft(s)} · ${sprintRange(s)}`
                        : s.state === 'planned'
                          ? `Starts ${asDayMonth(s.startsOn)}`
                          : sprintRange(s);
                    const shownGroups = groups.filter((g) => g.items.length);
                    if (q && shownGroups.length === 0) {
                      return <div className="board-sprint-none">No sprint matches “{sprintQuery}”.</div>;
                    }
                    return shownGroups.map((g) => (
                        <div key={g.head} className="board-sprint-sec">
                          <div className="board-sprint-group">{g.head}</div>
                          {g.items.map((s) => (
                            <button
                              key={s.id}
                              role="menuitemradio"
                              aria-checked={sprintPick === s.id}
                              className={`board-sprint-opt${sprintPick === s.id ? ' on' : ''}`}
                              onClick={() => {
                                pickSprint(s.id);
                                setSprintOpen(false);
                              }}
                            >
                              <span className="board-sprint-tick" aria-hidden="true">
                                {sprintPick === s.id ? '✓' : ''}
                              </span>
                              <span className="board-sprint-mark" aria-hidden="true">
                                <span className={`bs-dot ${s.state}`} />
                              </span>
                              <span className="board-sprint-nm">{s.name}</span>
                              {/* The one in flight, marked unmistakably in a long list. */}
                              {s.state === 'active' && <span className="bs-live">Live now</span>}
                              <span className="board-sprint-meta">{metaOf(s)}</span>
                              <span className="count">{onTheBoard(inSprint(visible, s.id)).length}</span>
                            </button>
                          ))}
                        </div>
                      ));
                  })()}
                </div>
              )}
            </div>
          </>
        )}
        {meta.cadence === 'sprints' && (
          /* The sprint context moved here from the old Next-up heading — how long is
             left, or that none is running, with the way into the sprint view, beside
             the Sprint control it describes. Shown even with no sprints yet, where
             "Plan a sprint" is the nudge that matters most. */
          <span className="nu-sprint-ctx">
            {thisSprint ? (
              <>
                {timeLeft(thisSprint)} ·{' '}
                <button className="link" onClick={() => setView('sprints')}>Open the sprint</button>
              </>
            ) : (
              <>
                No sprint running ·{' '}
                <button className="link" onClick={() => setView('sprints')}>Plan a sprint</button>
              </>
            )}
          </span>
        )}
        <span className="board-tools-lbl">Group by</span>
        <div className="seg board-group-seg" role="group" aria-label="Group the board into swimlanes">
          <button
            className={`seg-btn ${groupBy === 'none' ? 'active' : ''}`}
            aria-pressed={groupBy === 'none'}
            onClick={() => setGroup('none')}
          >
            None
          </button>
          <button
            className={`seg-btn ${groupBy === 'assignee' ? 'active' : ''}`}
            aria-pressed={groupBy === 'assignee'}
            onClick={() => setGroup('assignee')}
          >
            Accountable
          </button>
          <button
            className={`seg-btn ${groupBy === 'responsible' ? 'active' : ''}`}
            aria-pressed={groupBy === 'responsible'}
            onClick={() => setGroup('responsible')}
          >
            Responsible
          </button>
          <button
            className={`seg-btn ${groupBy === 'phase' ? 'active' : ''}`}
            aria-pressed={groupBy === 'phase'}
            onClick={() => setGroup('phase')}
          >
            Phase
          </button>
          <button
            className={`seg-btn ${groupBy === 'epic' ? 'active' : ''}`}
            aria-pressed={groupBy === 'epic'}
            onClick={() => setGroup('epic')}
          >
            Epic
          </button>
          <button
            className={`seg-btn ${groupBy === 'area' ? 'active' : ''}`}
            aria-pressed={groupBy === 'area'}
            onClick={() => setGroup('area')}
          >
            Area
          </button>
          <button
            className={`seg-btn ${groupBy === 'label' ? 'active' : ''}`}
            aria-pressed={groupBy === 'label'}
            onClick={() => setGroup('label')}
          >
            Label
          </button>
        </div>
        {/* The collapsed Group-by for a narrow desktop window — the same six options as
            the segmented control, as one "Group: … ▾" dropdown. Both sit in the DOM; the
            .is-tight class decides which one shows, so nothing is lost on a wide monitor. */}
        <div className="board-group-wrap" ref={groupWrap}>
          <button
            className={`tb-btn${groupOpen ? ' open' : ''}${groupBy !== 'none' ? ' is-set' : ''}`}
            aria-haspopup="menu"
            aria-expanded={groupOpen}
            title="Group the board into swimlanes"
            onClick={() => setGroupOpen((o) => !o)}
          >
            <Icon name={GROUP_ICON[groupBy]} />
            <span className="board-sort-cur">Group: {GROUP_LABEL[groupBy]}</span>
          </button>
          {groupOpen && (
            <div className="board-sort-menu" role="menu" aria-label="Group the board into swimlanes">
              {(Object.keys(GROUP_LABEL) as BoardGroup[]).map((g) => (
                <button
                  key={g}
                  role="menuitemradio"
                  aria-checked={groupBy === g}
                  className={`board-sort-opt${groupBy === g ? ' on' : ''}`}
                  onClick={() => {
                    setGroup(g);
                    setGroupOpen(false);
                  }}
                >
                  <span className="board-sort-tick" aria-hidden="true">{groupBy === g ? '✓' : ''}</span>
                  {GROUP_LABEL[g]}
                </button>
              ))}
            </div>
          )}
        </div>
        {groupBy !== 'none' && (
          <div className="board-focus-wrap" ref={focusWrap}>
            <button
              className={`tb-btn${focusOpen ? ' open' : ''}${focusLane ? ' is-set' : ''}`}
              aria-haspopup="menu"
              aria-expanded={focusOpen}
              title={`Show one ${groupBy} at a time`}
              onClick={() => setFocusOpen((o) => !o)}
            >
              {focusLane ? (
                <>
                  {laneMark(focusLane)}
                  <span className="board-focus-cur">{focusLane.label}</span>
                  <span
                    className="board-focus-clear"
                    role="button"
                    tabIndex={0}
                    aria-label={`Show ${GROUP_ALL[groupBy].toLowerCase()} again`}
                    title={`Show ${GROUP_ALL[groupBy].toLowerCase()} again`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFocus('');
                    }}
                  >
                    ✕
                  </span>
                </>
              ) : (
                <>
                  <Icon name={GROUP_ICON[groupBy]} />
                  <span className="board-focus-cur">{GROUP_ALL[groupBy]}</span>
                </>
              )}
            </button>
            {focusOpen && (
              <div className="board-focus-menu" role="menu" aria-label={`Show one ${groupBy} at a time`}>
                <input
                  className="board-focus-look"
                  value={focusLook}
                  placeholder={GROUP_SEARCH[groupBy]}
                  aria-label={GROUP_SEARCH[groupBy]}
                  autoFocus
                  onChange={(e) => setFocusLook(e.target.value)}
                />
                <button
                  role="menuitemradio"
                  aria-checked={!focus}
                  className={`board-focus-opt${!focus ? ' on' : ''}`}
                  onClick={() => {
                    setFocus('');
                    setFocusOpen(false);
                    setFocusLook('');
                  }}
                >
                  <span className="board-focus-tick" aria-hidden="true">{!focus ? '✓' : ''}</span>
                  <span className="board-focus-av all" aria-hidden="true">
                    <Icon name={GROUP_ICON[groupBy]} size={12} />
                  </span>
                  {GROUP_ALL[groupBy]}
                  <span className="count">{boardCards.length}</span>
                </button>
                <div className="board-focus-sep" role="separator" />
                {lanes
                  .filter((l) => l.label.toLowerCase().includes(focusLook.trim().toLowerCase()))
                  .map((l) => (
                    <button
                      key={l.key}
                      role="menuitemradio"
                      aria-checked={focus === l.key}
                      className={`board-focus-opt${focus === l.key ? ' on' : ''}`}
                      onClick={() => {
                        setFocus(l.key);
                        setFocusOpen(false);
                        setFocusLook('');
                      }}
                    >
                      <span className="board-focus-tick" aria-hidden="true">{focus === l.key ? '✓' : ''}</span>
                      {laneMark(l) ?? <span className="board-focus-av all" aria-hidden="true" />}
                      {l.label}
                      <span className="count">{l.rows.length}</span>
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}
        <div className="board-sort-wrap" ref={sortWrap}>
          <button
            className={`tb-btn${sortOpen ? ' open' : ''}`}
            aria-haspopup="menu"
            aria-expanded={sortOpen}
            title="Sort the cards in each column"
            onClick={() => setSortOpen((o) => !o)}
          >
            <Icon name="sort" />
            <span className="board-sort-cur">{SORT_LABEL[sortBy]}</span>
          </button>
          {sortOpen && (
            <div className="board-sort-menu" role="menu" aria-label="Sort the cards in each column">
              {(Object.keys(SORT_LABEL) as BoardSort[]).map((s) => (
                <button
                  key={s}
                  role="menuitemradio"
                  aria-checked={sortBy === s}
                  className={`board-sort-opt${sortBy === s ? ' on' : ''}`}
                  onClick={() => {
                    setSort(s);
                    setSortOpen(false);
                  }}
                >
                  <span className="board-sort-tick" aria-hidden="true">{sortBy === s ? '✓' : ''}</span>
                  {SORT_LABEL[s]}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* SELECT — the explicit multi-select toggle (project-bulk-card-changes). A
            glyph-only button (its brand-tint fill and tooltip carry the meaning),
            sitting after Sort. On turns the whole board into a selection surface;
            the `S` key does the same. */}
        <button
          type="button"
          className={`tb-btn board-select-toggle${selectMode ? ' is-on' : ''}`}
          aria-pressed={selectMode}
          aria-label={selectMode ? 'Done selecting' : 'Select cards'}
          title={selectMode ? 'Done selecting (S)' : 'Select cards (S)'}
          onClick={() => toggleMode()}
        >
          <svg className="ic" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="4" />
            <path d="M8 12.5l2.5 2.5L16 9" strokeWidth="2" />
          </svg>
        </button>
        {/* Fold every lane to its header, or open them all back out. Only while the
            board is grouped into lanes — there is nothing to fold on the plain
            board. Pushed to the far end of the toolbar, away from the controls
            that change what the board shows. */}
        {grouped && (
          <button
            className="tb-btn board-collapse-all"
            title={allCollapsed ? 'Open every lane' : 'Fold every lane to its header'}
            onClick={foldAll}
          >
            <Icon name={allCollapsed ? 'expand' : 'collapse'} size={18} />
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
        )}
        {/* OVERFLOW — on a narrow desktop window the Collapse-all action folds in here so
            the row stays one line. Only present while grouped (there is nothing to fold
            otherwise); hidden unless .is-tight. */}
        {grouped && (
          <div className="board-more-wrap" ref={moreWrap}>
            <button
              className={`tb-btn${moreOpen ? ' open' : ''}`}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              title="More board actions"
              onClick={() => setMoreOpen((o) => !o)}
            >
              <Icon name="ellipsis" />
              <span className="board-sort-cur">More</span>
            </button>
            {moreOpen && (
              <div className="board-sort-menu" role="menu" aria-label="More board actions">
                <button
                  role="menuitem"
                  className="board-sort-opt"
                  onClick={() => {
                    foldAll();
                    setMoreOpen(false);
                  }}
                >
                  <span className="board-sort-tick" aria-hidden="true" />
                  {allCollapsed ? 'Expand all lanes' : 'Collapse all lanes'}
                </button>
              </div>
            )}
          </div>
        )}
        {/* THE ALWAYS-ON BACKLOG CHIP (Vinnie, 30 Oct). The backlog lives off the board's
            columns, so on a busy board it used to vanish from sight entirely (the old
            .backlog-strip below only showed on an empty board). This keeps the queue
            permanently in view — pinned to the toolbar's right edge, shown whenever there
            is anything in the backlog, hidden at zero (no dead "0"). It counts only the
            backlog status, so it never touches the open/WIP numbers; one click opens the
            Backlog view. The optional in-board column is the separate Settings opt-in. */}
        {backlogCount > 0 && (
          <button
            type="button"
            className="tb-btn board-backlog-chip"
            title={`${backlogCount} in the backlog — open the backlog`}
            aria-label={`Backlog: ${backlogCount} ${backlogCount === 1 ? 'item' : 'items'}. Open the backlog.`}
            onClick={() => setView('backlog')}
          >
            <span className="status-dot dot-backlog" aria-hidden="true" />
            Backlog <span className="count">{backlogCount}</span>
            <Icon name="chevron" size={16} />
          </button>
        )}
      </div>

      <div className="wu-stage">
      {/* The filtered state, read at the board (not a hint far up the page):
          which sprint, its state, its dates, how many are here and hidden, and
          the one-click way back — so cause (the picker) and effect sit together. */}
      {filtering && (
        <div className="sprint-view-banner">
          <span className={`bs-dot ${pickedSprint!.state}`} aria-hidden="true" />
          {/* Adaptive: filtered to the running sprint, the name is already on the
              Next-up heading, so the banner drops it ("the live sprint") and the
              now-redundant state word. Filtered to a DIFFERENT sprint, it spells
              the name out with its state, so the two surfaces never read as one. */}
          <span className="svb-text">
            {pickedSprint!.id === thisSprint?.id ? (
              <>
                Viewing <b>the live sprint</b> · {sprintRange(pickedSprint!)} ·{' '}
                {onTheBoard(shown).length} here · {hiddenCount} hidden
              </>
            ) : (
              <>
                Viewing <b>{pickedSprint!.name}</b> · {SPRINT_STATE_LABEL[pickedSprint!.state]} ·{' '}
                {sprintRange(pickedSprint!)} · {onTheBoard(shown).length} here · {hiddenCount} hidden
              </>
            )}
          </span>
          <button className="btn btn-small svb-clear" onClick={() => pickSprint('')}>
            Show all sprints
          </button>
        </div>
      )}
      {/* STALE TO-DO STRIP — the board-face twin of the Insights Data quality row.
          A plain bordered notice (R12), above the columns, that a board-runner can
          act on in one tap or wave away for the session. */}
      {showStaleStrip && (
        <div className="stale-todo-strip">
          <span className="sts-ic" aria-hidden="true"><Icon name="package" size={15} /></span>
          <span className="sts-text">
            <b>
              {staleTodos.length} To-Do {staleTodos.length === 1 ? 'card looks' : 'cards look'} like backlog
            </b>{' '}
            — no due date, quiet {STALE_TODO_DAYS}+ days.
          </span>
          <button
            className="btn btn-small sts-move"
            disabled={staleBusy}
            onClick={() => void moveStaleToBacklog()}
          >
            Move {staleTodos.length} to Backlog
          </button>
          <button className="sts-x" aria-label="Not now" title="Not now" onClick={() => setStaleDismissed(true)}>
            ×
          </button>
        </div>
      )}
      {onlyIds && (
        /* After a partial bulk move, "Show it" narrows the board to the cards that
           could not move, so the one that is blocked is easy to find (R15). */
        <div className="board-onlyfilter" role="status">
          <svg className="ic" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M12 3 2 20h20L12 3zM12 10v4M12 17v.5" /></svg>
          <span>
            Showing the {onlyIds.size === 1 ? 'card' : `${onlyIds.size} cards`} that couldn’t move.
          </span>
          <button type="button" className="link" onClick={() => setOnlyIds(null)}>Show all cards</button>
        </div>
      )}
      {groupBy === 'none' ? (
        <BoardScroll>{cols.map((status) => column(status, onlyFilter(shown), 'all', canAdd))}</BoardScroll>
      ) : focusLane ? (
        /* Focused on one assignee — the ordinary board, just their cards. No lane
           band: the focus control already names who this is. */
        <BoardScroll>
          {cols.map((status) => column(status, onlyFilter(focusLane.rows), 'all', false))}
        </BoardScroll>
      ) : lanes.length === 0 ? (
        <p className="hint board-lanes-empty">No cards on the board to group yet.</p>
      ) : (
        <>
          {groupBy === 'label' && (
            <p className="board-label-note" role="note">
              A card with more than one label appears in each of its lanes, so a lane’s count is not a card count.
            </p>
          )}
          {lanes.map((lane) => {
          const shut = collapsedLanes.has(lane.key);
          return (
            <div className="lane" key={lane.key}>
              {/* The lane header is the fold control — the same 18px disclosure the
                  rest of the app uses (down when open, right when folded), in the
                  leading position, so folding reads the same wherever it appears. */}
              <button
                className={`lane-head${shut ? ' shut' : ''}${lane.done ? ' is-done' : ''}`}
                aria-expanded={!shut}
                onClick={() => toggleLane(lane.key)}
              >
                <span className="lane-chev" aria-hidden="true">
                  <Icon name="chevron" size={18} />
                </span>
                {lane.tint && (
                  <span className="lane-dot" style={{ background: lane.tint }} aria-hidden="true" />
                )}
                <span className="lane-name">{lane.label}</span>
                {(groupBy === 'assignee' || groupBy === 'responsible') && (
                  <VendorTag who={lane.key} profiles={profiles} compact />
                )}
                <span className="count">{lane.rows.length}</span>
                {lane.done && (
                  <span className="rag g lane-done-tag" title="Every card in this epic is done, so it sits at the bottom of the board">Complete</span>
                )}
              </button>
              <Expander open={!shut}>
                <BoardScroll>
                  {cols.map((status) => column(status, lane.rows, lane.key, false))}
                </BoardScroll>
              </Expander>
            </div>
          );
        })}
        </>
      )}
        <WheelsUp
          complete={boardComplete}
          signature={doneSignature}
          boardId={projectId}
          boardName={meta.name}
        />
      </div>
      </>
      )}
      {/* The floating bulk bar — appears only when ≥1 card is selected, docked
          bottom-centre of the board area (project-bulk-card-changes). View-agnostic:
          the Grid will mount this exact component. "Show it" reveals the refused. */}
      <BulkBar onShowRefused={(ids) => setOnlyIds(new Set(ids))} />
    </div>
    </AttentionChipsContext.Provider>
  );
}
